import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

// MUI Components
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid2 as Grid,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Paper,
  TextField,
  Typography
} from '@mui/material';

// MUI Icons
import {
  Add as AddIcon,
  ArrowDropDownOutlined as ArrowDropDownOutlinedIcon,
  ChangeCircleOutlined as ChangeCircleOutlinedIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  Error as ErrorIcon,
  ExpandMore as ExpandMoreIcon,
  InfoOutlined as InfoOutlinedIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";

// Contexts
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';

// Utilities & Helpers
import {
  calcRefFees,
  calcWfs,
  convertsLength,
  convertsMass,
  fetchNextDataWithRetry,
  fetchWrapper,
  fetchWrapperNoAuth,
  float2Decimal,
  mergeObjects,
  sendErrorMessage,
  SERVER
} from '../../utils/helpers';
import {
  checkFeed,
  checkItem,
  convertItemWFS,
  MAX_STEP_RETRIES
} from '../../utils/proccessHelpers';
import { BATTERY_SIZES, COUNTRIES_OF_ORIGIN, PRODUCT_TYPES } from './helpers';

// Components
import SchemaForm from './SchemaForm';

// Modals
import ErrorModal from '../Modals/ErrorModal';
import SafetyDataSheetsModal from '../Modals/SafetyDataSheetsModal';
import QuestionModal from '../Modals/QuestionModal';

// Tooltips & Loaders
import HtmlTooltip from '../Tooltip/HtmlTooltip';
import CustomCircularProgress from '../Loaders/CustomCircularProgress';
import CustomStepProgressMobile from '../Loaders/CustomStepProgressMobile';
import EnhancedSpinningLogo from '../Loaders/SpinningLogo';

// Inputs
import CustomInput from '../Inputs/CustomInput';
import SelectComponent from '../Inputs/SelectComponent';
import SelectCountries from '../Inputs/SelectCountries';
import SKUBuilderModal from '../Modals/SKUBuilderModal';
import ImagesModal from '../Modals/ImagesModal';
import ProductTypeSelector from '../Modals/ProductTypeSelector';
import CostPriceInput from '../Inputs/CostPriceInput';

const WarningInput = ({ type, isWarning, ...props }) => {
  const { theme } = useTheme();

  return (
    <TextField
      sx={{
        "& .MuiOutlinedInput-root": {
          "& fieldset": {
            // borderColor: customtheme.accent.warning, // Updated border color
            border: isWarning ? `2px solid ${theme.accent.warning}` : `1px solid ${theme.text.primary}`
          },
          "&:hover fieldset": {
            border: isWarning ? `2px solid ${theme.accent.warning}` : `1px solid ${theme.text.primary}`
          },
          "&.Mui-focused fieldset": {
            border: isWarning ? `2px solid ${theme.accent.warning}` : `1px solid ${theme.text.primary}`
          },
        },
        "& .MuiInputLabel-root": {
          color: isWarning ? theme.accent.warning : theme.text.primary, // Updated label color
          fontFamily: theme.fontFamily,
          "&.Mui-focused": {
            color: isWarning ? theme.accent.warning : theme.text.primary, // Updated focused label color
          },
        },
        "& .MuiOutlinedInput-input": {
          fontFamily: theme.fontFamily,
          color: theme.text.primary, // Updated input text color
        },
      }}
      {...props}
      label={<Box sx={{ mr: "8px" }}>{isWarning ? `${props.label} - optional` : props.label}</Box>}
      type={type}
      slotProps={{
        input: props.InputProps,
      }}
      customtheme={theme}
    />
  );
};

const SourceInput = ({ sources = [], isWarning, value, onChange }) => {
  const { theme } = useTheme();
  const handleChange = (event, newValue) => {
    // Call the parent's onChange with the new value
    if (onChange) {
      onChange(newValue);
    }
  };

  const styles = {
    formControl: {
      mb: 2,
      borderRadius: 1.5,
      '& .MuiOutlinedInput-root': {
        '&:hover fieldset': {
          borderColor: isWarning ? theme.accent.warning : theme.text.primary,
        },
        '&.Mui-focused fieldset': {
          borderColor: isWarning ? theme.accent.warning : theme.text.primary,
        }
      },
      "& .MuiInputLabel-root": {
        color: isWarning ? theme.accent.warning : theme.text.primary, // Updated label color
        fontFamily: theme.fontFamily,
        "&.Mui-focused": {
          color: isWarning ? theme.accent.warning : theme.text.primary, // Updated focused label color
        },
      },
      "& .MuiOutlinedInput-input": {
        fontFamily: theme.fontFamily,
        color: theme.text.primary,
      },
    },
    textField: {
      "& .MuiOutlinedInput-root": {
        "& fieldset": {
          // borderColor: customtheme.accent.warning, // Updated border color
          border: isWarning ? `2px solid ${theme.accent.warning}` : `1px solid ${theme.text.primary}`
        },
        "&:hover fieldset": {
          border: isWarning ? `2px solid ${theme.accent.warning}` : `1px solid ${theme.text.primary}`
        },
        "&.Mui-focused fieldset": {
          border: isWarning ? `2px solid ${theme.accent.warning}` : `1px solid ${theme.text.primary}`
        },
      },
      "& .MuiInputLabel-root": {
        color: isWarning ? theme.accent.warning : theme.text.primary, // Updated label color
        fontFamily: theme.fontFamily,
        "&.Mui-focused": {
          color: isWarning ? theme.accent.warning : theme.text.primary, // Updated focused label color
        },
      },
      "& .MuiOutlinedInput-input": {
        fontFamily: theme.fontFamily,
        color: theme.text.primary, // Updated input text color
      },
      ".MuiAutocomplete-clearIndicator": {
        color: isWarning ? theme.accent.warning : theme.text.primary
      },
    }
  }

  return (
    <FormControl fullWidth sx={styles.formControl}>
      <Autocomplete
        freeSolo
        options={sources.filter(item => !!item) || []}
        value={value && value !== 'null' && value !== 'undefined' ? value : ''}
        onChange={handleChange}
        inputValue={value && value !== 'null' && value !== 'undefined' ? value : ''}
        onInputChange={handleChange}
        renderInput={(params) => (
          <TextField
            {...params}
            sx={styles.textField}
            label={<Box sx={{ mr: "8px" }}>{isWarning ? 'Source - optional' : 'Source'}</Box>}
            variant="outlined"
            size="small"
          />
        )}
        renderOption={(props, option) => (
          <Box key={option} component="li" {...props}>
            <Box>
              <Typography variant="body2" sx={{ fontFamily: theme.fontFamily }}>
                {option}
              </Typography>
            </Box>
          </Box>
        )}
      />
    </FormControl>
  );
};

const WarningEvaluateInput = ({ type, isWarning, onExpressionEvaluated, ...props }) => {
  const { theme } = useTheme();
  const [displayValue, setDisplayValue] = useState(props.value || "");

  // Safe math expression evaluator
  const evaluateExpression = (expression) => {
    try {
      // Only allow numbers, operators, parentheses, and decimal points
      const safeExpression = expression.replace(/[^0-9+\-*/.() ]/g, '');

      // Prevent empty or invalid expressions
      if (!safeExpression || safeExpression.trim() === '') {
        return expression;
      }

      // Use Function constructor instead of eval for better security
      const result = new Function('return ' + safeExpression)();

      // Check if result is a valid number
      if (typeof result === 'number' && !isNaN(result)) {
        return result.toFixed(3);
      }

      return expression; // Return original if evaluation fails
    } catch (error) {
      return expression; // Return original expression if there's an error
    }
  };

  const handleBlur = (e) => {
    const evaluatedValue = evaluateExpression(displayValue);
    setDisplayValue(evaluatedValue);

    // Call the original onChange with evaluated value
    if (props.onChange) {
      const syntheticEvent = {
        ...e,
        target: { ...e.target, value: evaluatedValue }
      };
      props.onChange(syntheticEvent);
    }

    // Call onExpressionEvaluated if provided
    if (onExpressionEvaluated) {
      onExpressionEvaluated(evaluatedValue);
    }

    // Call original onBlur if provided
    if (props.onBlur) {
      props.onBlur(e);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.target.blur(); // This will trigger handleBlur
    }

    // Call original onKeyPress if provided
    if (props.onKeyPress) {
      props.onKeyPress(e);
    }
  };

  const handleChange = (e) => {
    setDisplayValue(e.target.value);

    // Don't call the original onChange here - we'll do it on blur
    // This prevents the parent state from updating with partial expressions
  };

  // Update local state when props.value changes
  useEffect(() => {
    setDisplayValue(props.value || "");
  }, [props.value]);

  return (
    <TextField
      {...props}
      value={displayValue}
      onChange={handleChange}
      onBlur={handleBlur}
      onKeyPress={handleKeyPress}
      sx={{
        "& .MuiOutlinedInput-root": {
          "& fieldset": {
            border: isWarning ? `2px solid ${theme.accent.warning}` : `1px solid ${theme.text.primary}`
          },
          "&:hover fieldset": {
            border: isWarning ? `2px solid ${theme.accent.warning}` : `1px solid ${theme.text.primary}`
          },
          "&.Mui-focused fieldset": {
            border: isWarning ? `2px solid ${theme.accent.warning}` : `1px solid ${theme.text.primary}`
          },
        },
        "& .MuiInputLabel-root": {
          color: isWarning ? theme.accent.warning : theme.text.primary,
          fontFamily: theme.fontFamily,
          "&.Mui-focused": {
            color: isWarning ? theme.accent.warning : theme.text.primary,
          },
        },
        "& .MuiOutlinedInput-input": {
          fontFamily: theme.fontFamily,
          color: theme.text.primary,
        },
      }}
      label={<Box sx={{ mr: "8px" }}>{isWarning ? `${props.label} - optional` : props.label}</Box>}
      type={type}
      slotProps={{
        input: props.InputProps,
      }}
    />
  );
};

const SKU_BUILDER_LIST = [
  { selected: true, name: "Source" },
  { selected: true, name: "Cost Price" },
  { selected: true, name: "Selling Price" },
  { selected: false, name: "Barcode" },
  { selected: false, name: "Item ID" },
  { selected: true, name: "Brand" },
  { selected: true, name: "Quantity" },
  { selected: true, name: "Profit" },
  { selected: true, name: "Breakeven" },
  { selected: false, name: "Expiration Date" },
  { selected: false, name: "Purchase Date" },
  { selected: false, name: "Note" },
  { selected: false, name: "Random No." }
];

function MobileForm(props) {
  const location = useLocation();
  const { theme } = useTheme();
  const { encodedAuth, user, password } = useAuth();

  const currentItemRef = useRef(null);

  const [currProduct, setCurrProduct] = useState({});
  const [addedItem, setAddedItem] = useState({});

  const [openSkuBuilderModal, setOpenSkuBuilderModal] = useState(false);
  const [openErrorModal, setopenErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [openQuestionModal, setOpenQuestionModal] = useState(false);
  const [openImgModal, setOpenImgModal] = useState(false);
  const [sfInventory, setSfInventory] = useState(0);

  const [schema, setSchema] = useState(Object.keys(currProduct?.schema || {}).includes('$schema') ? currProduct?.schema : null);
  const [formData, setFormData] = useState(currProduct?.MPItem?.Visible || currProduct?.mp_item?.Visible || {});

  const [inProgress, setInProgress] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [loadingAddItem, setLoadingAddItem] = useState(false);

  const [productType, setProductType] = useState("");
  const [productTypeInput, setProductTypeInput] = useState("");
  const [additionalCosts, setAdditionalCosts] = useState("");

  // Dimensions
  const [weight, setWeight] = useState("");
  const [weightUnit, setWeightUnit] = useState("lbs");
  const [anchorElWeight, setAnchorElWeight] = useState("");
  const openWeight = Boolean(anchorElWeight);

  const [length, setLength] = useState("");
  const [anchorElLength, setAnchorElLength] = useState("");
  const openLength = Boolean(anchorElLength);
  const [width, setWidth] = useState("");
  const [anchorElWidth, setAnchorElWidth] = useState("");
  const openWidth = Boolean(anchorElWidth);
  const [height, setHeight] = useState("");
  const [anchorElHeight, setAnchorElHeight] = useState("");
  const openHeight = Boolean(anchorElHeight);

  const [dimensionsUnit, setDimensionsUnit] = useState("inch");

  const [source, setSource] = useState(localStorage.getItem('IFSource') || "");
  const [sources, setSources] = useState(JSON.parse(localStorage.getItem('IFSources') || "[]") || []);
  const [cost, setCost] = useState("");
  const [expirationDate, setExpirationDate] = useState("");
  const [expirationDateInputType, setExpirationDateInputType] = useState('text');
  const [purchaseDate, setPurchaseDate] = useState(new Date().toLocaleDateString());
  const [purchaseDateInputType, setPurchaseDateInputType] = useState('text');
  const [note, setNote] = useState("");

  // SupplierItem
  // Orderable
  const [sku, setSku] = useState("");
  const [skuUpdate, setSkuUpdate] = useState(false);
  const [skuFound, setSkuFound] = useState(false);
  const [brand, setBrand] = useState("");
  const [barcode, setBarcode] = useState("");
  const [changeBarcode, setChangeBarcode] = useState(false);
  const [price, setPrice] = useState("");
  const [qty, setQty] = useState(1);
  const [caseSetup, setCaseSetup] = useState('individual');
  const [innerPack, setInnerPack] = useState(1);
  const [electronicsIndicator, setElectronicsIndicator] = useState("No");
  const [chemicalAerosolPesticide, setChemicalAerosolPesticide] = useState("No");
  const [safetyDataSheets, setSafetyDataSheets] = useState([]);
  const [safetyDataSheetOptions, setSafetyDataSheetOptions] = useState([]);
  const [numberOfHazardousComponents, setNumberOfHazardousComponents] = useState("");
  const [openSafetyModal, setOpenSafetyModal] = useState(false);
  const [batteryTechnologyType, setBatteryTechnologyType] = useState("Does Not Contain a Battery");
  const [hasBatteries, setHasBatteries] = useState("No");
  const [batterySize, setBatterySize] = useState("");
  const [batteryModel, setBatteryModel] = useState("");
  const [batteryFormFactor, setBatteryFormFactor] = useState("");
  const [numberOfBatteries, setNumberOfBatteries] = useState(0);
  const [numberOfBatteryCells, setNumberOfBatteryCells] = useState(0);
  const [includedBatteryPackaging, setIncludedBatteryPackaging] = useState("");
  const [batteryWeight, setBatteryWeight] = useState(0.00); // * 0.001
  const [batteryWattHour, setBatteryWattHour] = useState(0.00); // * 0.001
  const [lithiumMetalContentWeight, setLithiumMetalContentWeight] = useState(0.00); // * 0.001
  const [labelImage, setLabelImage] = useState([]);
  const [labelImageContains, setLabelImageContains] = useState("");
  const [labelImageURL, setLabelImageURL] = useState("");
  const [requiredStorageCondition, setRequiredStorageCondition] = useState("");

  const [stateRestrictions, setStateRestrictions] = useState([]);
  const [stateRestrictionsText, setStateRestrictionsText] = useState("None");
  const [stateRestrictionsStates, setStateRestrictionsStates] = useState("");
  const [stateRestrictionsZipCodes, setStateRestrictionsZipCodes] = useState("");

  const isFirstRender = useRef(true);

  const [countryOfOrigin, setCountryOfOrigin] = useState(localStorage.getItem('IFCountryOfOrigin') || "US - United States");

  const [skuInputs, setSkuInputs] = useState(() => {
    try {
      const storedOrder = localStorage.getItem('IFSKUInputs');
      return storedOrder ? JSON.parse(storedOrder) : SKU_BUILDER_LIST;
    } catch (error) {
      return SKU_BUILDER_LIST;
    }
  });
  const [skuSeperator, setSkuSeperator] = useState(() => {
    try {
      const storedSeperator = localStorage.getItem('IFSKUSeperator');
      return storedSeperator ? storedSeperator : "_";
    } catch (error) {
      return "_";
    }
  });

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    setSchema(null);
    setFormData({});
    loadSchema(currProduct.productType || currProduct?.anItem?.productType);
  }, [skuUpdate]);

  const resetSkuInputs = () => {
    setSkuInputs(SKU_BUILDER_LIST);
    localStorage.removeItem("IFSKUInputs");
  };

  const handleWeightUnitChange = (unit) => {
    const newWeight = convertsMass(parseFloat(weight), weightUnit, unit);
    setWeight(newWeight.toFixed(2));
    setWeightUnit(unit);
    setAnchorElWeight(null);
  };

  const handleDimensionsUnitChange = (unit) => {
    const newLength = convertsLength(parseFloat(length), dimensionsUnit, unit);
    const newWidth = convertsLength(parseFloat(width), dimensionsUnit, unit);
    const newHeight = convertsLength(parseFloat(height), dimensionsUnit, unit);
    setLength(newLength.toFixed(2));
    setWidth(newWidth.toFixed(2));
    setHeight(newHeight.toFixed(2));
    setDimensionsUnit(unit);
    setAnchorElLength(null);
    setAnchorElWidth(null);
    setAnchorElHeight(null);
  };

  const alertError = (msg) => {
    setErrorMessage(msg);
    setopenErrorModal(true);
  };

  const buildLink = (item) => {
    return `http://msds.walmartstores.com/client/document?productid=${item.ProductID}&productguid=${item.ProductGuid}&DocumentKey=undefined&HazdocumentKey=undefined&MSDS=0&subformat=NAM`;
  };

  function isValidURL(url) {
    try {
      new URL(url);
      return true;
    } catch (e) {
      return false;
    }
  };

  const validateSchemaForm = (schema, productType, formData) => {
    // Result object
    const result = {
      isValid: true,
      errors: []
    };

    // Check if schema, productType, and formData exist
    if (!schema || !schema.properties || !schema.properties[productType] || !formData || !formData[productType]) {
      result.isValid = false;
      result.errors.push('Invalid schema or form data');
      return result;
    }

    const productSchema = schema.properties[productType];
    const productData = formData[productType];

    // 1. Check static required fields
    const requiredFields = productSchema.required || [];

    for (const fieldName of requiredFields) {
      if (!validateField(productSchema.properties[fieldName], productData[fieldName])) {
        result.isValid = false;
        result.errors.push(`Required field "${fieldName}" is missing or invalid`);
      }
    }

    // 2. Check conditional required fields (from allOf conditions)
    const allOfConditions = productSchema.allOf || [];

    allOfConditions.forEach(condition => {
      // Check if the condition "if" clause matches current form data
      if (condition.if && condition.if.properties && condition.then && condition.then.required) {
        let conditionMet = true;

        // Check each property in the "if" condition
        Object.entries(condition.if.properties).forEach(([fieldName, expectedValue]) => {
          const currentValue = productData[fieldName];

          // If any condition doesn't match, mark as not met
          if (expectedValue.enum) {
            // Handle enum conditions (match any value in enum)
            if (Array.isArray(currentValue)) {
              for (let i = 0; i < currentValue.length; i++) {
                if (!expectedValue.enum.includes(currentValue[i])) {
                  conditionMet = false;
                  break;
                }
              }
            } else {
              if (!expectedValue.enum.includes(currentValue)) {
                conditionMet = false;
              }
            }
          } else if (expectedValue.const !== undefined && currentValue !== expectedValue.const) {
            // Handle const conditions (exact match)
            conditionMet = false;
          } else if (expectedValue?.contains?.enum) {
            if (Array.isArray(currentValue)) {
              for (let i = 0; i < currentValue.length; i++) {
                if (!expectedValue?.contains?.enum.includes(currentValue[i])) {
                  conditionMet = false;
                  break;
                }
              }
            } else {
              if (!expectedValue?.contains?.enum.includes(currentValue)) {
                conditionMet = false;
              }
            }
          }
        });

        // If all conditions are met, validate the "then.required" fields
        if (conditionMet) {
          condition.then.required.forEach(fieldName => {
            if (!validateField(productSchema.properties[fieldName], productData[fieldName])) {
              result.isValid = false;
              result.errors.push(`Conditionally required field "${fieldName}" is missing or invalid`);
            }
          });
        }
      }
    });

    return result;
  };

  const validateField = (fieldSchema, fieldValue) => {
    if (!fieldSchema) return false;

    // Handle different field types
    switch (fieldSchema.type) {
      case 'string':
        // Check if the string exists and meets length requirements
        if (fieldValue === undefined || fieldValue === null || fieldValue === '') {
          return false;
        }
        if (fieldSchema.minLength && fieldValue.length < fieldSchema.minLength) {
          return false;
        }
        if (fieldSchema.maxLength && fieldValue.length > fieldSchema.maxLength) {
          return false;
        }
        // If this is an enum, check if the value is in the allowed list
        if (fieldSchema.enum && !fieldSchema.enum.includes(fieldValue)) {
          return false;
        }
        return true;

      case 'number':
      case 'integer':
        // Check if the number exists and meets range requirements
        if (fieldValue === undefined || fieldValue === null || isNaN(fieldValue)) {
          return false;
        }
        if (fieldSchema.minimum !== undefined && fieldValue < fieldSchema.minimum) {
          return false;
        }
        if (fieldSchema.maximum !== undefined && fieldValue > fieldSchema.maximum) {
          return false;
        }
        return true;

      case 'boolean':
        // For boolean, just check if it's defined (can be true or false)
        return fieldValue !== undefined && fieldValue !== null;

      case 'array':
        // Check if array exists and meets length requirements
        if (!Array.isArray(fieldValue) || fieldValue.length === 0) {
          return false;
        }
        if (fieldSchema.minItems && fieldValue.length < fieldSchema.minItems) {
          return false;
        }
        if (fieldSchema.maxItems && fieldValue.length > fieldSchema.maxItems) {
          return false;
        }
        // Validate each item in the array if needed
        if (fieldSchema.items) {
          for (const item of fieldValue) {
            if (!validateField(fieldSchema.items, item)) {
              return false;
            }
          }
        }
        return true;

      case 'object':
        // Check if object exists
        if (!fieldValue || typeof fieldValue !== 'object') {
          return false;
        }
        // Validate required properties if any
        if (fieldSchema.required) {
          for (const requiredProp of fieldSchema.required) {
            if (!validateField(
              fieldSchema.properties[requiredProp],
              fieldValue[requiredProp]
            )) {
              return false;
            }
          }
        }
        // For complex objects like measures with units
        if (fieldSchema.properties) {
          if (fieldSchema.properties.measure && fieldValue.measure === undefined) {
            return false;
          }
          if (fieldSchema.properties.unit && fieldValue.unit === undefined) {
            return false;
          }
        }
        return true;

      default:
        return false;
    }
  };

  const checkAllData = () => {
    if (!barcode || barcode.length > 14) {
      alertError("The Barcode should be between 12 and 14 characters long. Please enter a valid Barcode.");
      return false;
    }
    if (!sku || sku.length < 1 || sku.length > 50) {
      alertError("The SKU should be between 1 and 50 characters long. Please enter a valid SKU.");
      return false;
    }
    if (!price || parseFloat(price) <= 0) {
      alertError("The Selling Price should be a possitive number. Please enter a valid Selling Price.");
      return false;
    }
    if (!qty || parseInt(qty) <= 0) {
      alertError("The Quantity should be a possitive number. Please enter a valid Quantity.");
      return false;
    }
    if (caseSetup === "case") {
      if (!innerPack || parseInt(innerPack) <= 0) {
        alertError("The units per case should be a possitive number. Please enter a valid units per case.");
        return false;
      }
      if (parseInt(qty) % parseInt(innerPack) !== 0) {
        alertError(`The quantity (${qty}) must be evenly divisible by the inner pack (${innerPack}).`);
        return false;
      }
    }
    if (!weight || parseFloat(weight) <= 0) {
      alertError("The Weight should be a possitive number. Please enter a valid Weight.");
      return false;
    }
    if (!brand || sku.length < 1 || sku.length > 60) {
      alertError("The Brand should be between 1 and 60 characters long. Please enter a valid Brand.");
      return false;
    }
    if (!currProduct.title && !currProduct.productName) {
      alertError("Title is required. Please enter a product title.");
      return false;
    }
    if (!length || parseFloat(length) <= 0) {
      alertError("The Length should be a possitive number. Please enter a valid Length.");
      return false;
    }
    if (!width || parseFloat(width) <= 0) {
      alertError("The Width should be a possitive number. Please enter a valid Width.");
      return false;
    }
    if (!height || parseFloat(height) <= 0) {
      alertError("The Height should be a possitive number. Please enter a valid Height.");
      return false;
    }

    // Check dependencies
    if (chemicalAerosolPesticide === "Yes") {
      if (safetyDataSheets.length === 0) {
        setOpenSafetyModal(true);
        alertError("Since the product is classified as a chemical aerosol pesticide, a Safety Data Sheet is required.");
        return false;
      } else if (!safetyDataSheets.every(isValidURL)) {
        setOpenSafetyModal(true);
        alertError("Data provided in field 'safetyDataSheet' is not in URL format");
        return false;
      }
      if (!numberOfHazardousComponents || parseInt(numberOfHazardousComponents) < 1 || parseInt(numberOfHazardousComponents) > 1000) {
        alertError("Since the product is a chemical aerosol pesticide, you must specify the number of hazardous components. Please enter a number between 1 and 1,000.");
        return false;
      }
      if (labelImage.length === 0) {
        setOpenImgModal(true);
        alertError("Since the product is classified as a chemical aerosol pesticide, a Label Image is required.");
        return false;
      } else if (labelImage.findIndex(item => item.labelImageContains === "Drug Facts") !== -1 && !requiredStorageCondition) {
        alertError("Since Label Image Contains Drug Facts, a Required Storage Condition is required.");
        return false;
      }
    } else if (["Lithium Ion", "Lithium Primary (Lithium Metal)", "Lead Acid", "Lead Acid (Non-Spillable)"].includes(batteryTechnologyType)) {
      if (!numberOfHazardousComponents || parseInt(numberOfHazardousComponents) < 1 || parseInt(numberOfHazardousComponents) > 1000) {
        alertError("Since the product is a chemical aerosol pesticide, you must specify the number of hazardous components. Please enter a number between 1 and 1,000.");
        return false;
      }
    }
    if (batteryTechnologyType !== "Does Not Contain a Battery") {
      if (!batterySize) {
        alertError("If contains a battery, 'Battery Size' is required.");
        return false;
      }
      if (hasBatteries === "No") {
        alertError("If contains a battery, 'Batteries Included' should be 'Yes'.");
        return false;
      }
      if (["Lithium Ion", "Lithium Primary (Lithium Metal)"].includes(batteryTechnologyType)) {
        if (!batteryModel || batteryModel.length < 1 || batteryModel.length > 400) {
          alertError(`Since the contained battery type is '${batteryTechnologyType}', 'Battery Model' is required. The Battery Model should be between 1 and 400 characters long`);
          return false;
        }
        if (!batteryFormFactor) {
          alertError(`Since the contained battery type is '${batteryTechnologyType}', 'Battery Form Factor' is required.`);
          return false;
        }
        if (!numberOfBatteries || parseInt(numberOfBatteries) <= 0) {
          alertError(`Since the contained battery type is '${batteryTechnologyType}', 'Battery Count' is required. Please enter a number between 1 and 10,000,000.`);
          return false;
        }
        if (!numberOfBatteryCells || parseInt(numberOfBatteryCells) <= 0) {
          alertError(`Since the contained battery type is '${batteryTechnologyType}', 'Battery Cell Count' is required. Please enter a number between 1 and 1,000.`);
          return false;
        }
        if (!includedBatteryPackaging) {
          alertError(`Since the contained battery type is '${batteryTechnologyType}', 'Included Battery Packaging' is required.`);
          return false;
        }
        if (!batteryWeight || parseFloat(batteryWeight) <= 0) {
          alertError(`Since the contained battery type is '${batteryTechnologyType}', 'Battery Weight' is required. The Battery Weight should be a possitive number.`);
          return false;
        }
        if (batteryTechnologyType === "Lithium Ion" && (!batteryWattHour || parseFloat(batteryWattHour) <= 0)) {
          alertError("Since the contained battery type is 'Lithium Ion', 'Battery Watt Hour Rating' is required. The Battery Watt Hour Rating should be a possitive number.");
          return false;
        }
        if (batteryTechnologyType === "Lithium Primary (Lithium Metal)" && (!lithiumMetalContentWeight || parseInt(lithiumMetalContentWeight) <= 0)) {
          alertError("Since the contained battery type is 'Lithium Primary (Lithium Metal)', 'Lithium Metal Content Weight' is required. The Lithium Metal Content Weight should be a possitive number.");
          return false;
        }
      }
    }

    const result = validateSchemaForm(schema, productType, formData);
    if (!result.isValid) {
      alertError(
        <>
          <ul style={{ textAlign: 'left' }}>
            {result.errors.map(err => (
              <li>{err}</li>
            ))}
          </ul>
        </>
      );
      return false;
    }
    return true;
  };

  const padCodeToGTIN = (code) => {
    if (code.length < 14) {
      return code.padStart(14, '0');
    }
    return code;
  };

  const handleZeroSFQty = async () => {
    const body = {
      sku: sku.trim(),
      qty: 0
    }
    try {
      await fetchWrapper(`${SERVER}/put_update_inventory`, user, password, encodedAuth, { method: 'POST', body: body });
    } catch (error) {
      console.error('Error update batch:', error.message);
    } finally {
      await addItem();
    }
  };

  const handleAddItem = async () => {
    setLoadingAddItem(true);
    if (!checkAllData()) {
      setLoadingAddItem(false);
      return;
    }

    const bodyInventory = {
      sku: sku.trim(),
      shipNode: undefined
    }
    try {
      const inventoryItem = await fetchWrapper(`${SERVER}/get_inventory`, user, password, encodedAuth, { method: 'POST', body: bodyInventory });
      if (inventoryItem.quantity.amount > 0) {
        setSfInventory(inventoryItem.quantity.amount);
        setOpenQuestionModal(true);
      } else {
        addItem();
      }
    } catch (error) {
      addItem();
    }
  };

  const addItem = async () => {
    setInProgress(true);
    setLoadingAddItem(false);
    localStorage.setItem('IFCountryOfOrigin', countryOfOrigin);
    localStorage.setItem('IFSource', source);
    const updatedSources = Array.from(new Set([source, ...sources])); // Remove duplicates
    setSources(updatedSources);
    localStorage.setItem('IFSources', JSON.stringify(updatedSources));

    let code = padCodeToGTIN(barcode);

    const gtin = currProduct.gtin || code || barcode;
    const upc = (currProduct.upc || barcode).padStart(12, '0');

    const futureDate = new Date();
    futureDate.setFullYear(futureDate.getFullYear() + 25);

    const MPItem = {
      Orderable: {
        sku: sku.slice(0, 50).trim(),                       // maxLength: 50
        productIdentifiers: {
          productId: gtin,                                  // length: 14
          productIdType: 'GTIN'                             // enum
        },
        price: float2Decimal(price),                        // maximum: 10000000000000000
        stateRestrictions: stateRestrictions.length ? stateRestrictions : [{ 'stateRestrictionsText': "None" }],
        chemicalAerosolPesticide: chemicalAerosolPesticide, // enum
        electronicsIndicator: electronicsIndicator,         // enum
        batteryTechnologyType: batteryTechnologyType,       // enum
        endDate: futureDate.toISOString(),
      },
      Visible: formData,
      TradeItem: {
        sku: sku.slice(0, 50).trim(),                      // maxLength: 50
        countryOfOriginAssembly: [countryOfOrigin], // enum
        orderableGTIN: gtin,                        // length: 14
        each: {
          eachGTIN: gtin,                           // length: 14
          eachDepth: float2Decimal(convertsLength(parseFloat(length), dimensionsUnit, "inch")),  // maximum: 1000000000
          eachWidth: float2Decimal(convertsLength(parseFloat(width), dimensionsUnit, "inch")),   // maximum: 1000000000
          eachHeight: float2Decimal(convertsLength(parseFloat(height), dimensionsUnit, "inch")), // maximum: 1000000000
          eachWeight: float2Decimal(convertsMass(parseFloat(weight), weightUnit, 'lbs'))         // maximum: 100000000000
        }
      }
    };

    if (skuUpdate) {
      MPItem.Orderable.SkuUpdate = "Yes";
    }

    // Check dependencies
    if (chemicalAerosolPesticide === "Yes") {
      MPItem.Orderable.safetyDataSheet = safetyDataSheets;
      MPItem.Orderable.numberOfHazardousComponents = parseInt(numberOfHazardousComponents);
      MPItem.Orderable.labelImage = labelImage;
      if (labelImage.findIndex(item => item.labelImageContains === "Drug Facts") !== -1) {
        MPItem.Orderable.required_storage_condition = requiredStorageCondition;
      }
    } else if (["Lithium Ion", "Lithium Primary (Lithium Metal)", "Lead Acid", "Lead Acid (Non-Spillable)"].includes(batteryTechnologyType)) {
      MPItem.Orderable.numberOfHazardousComponents = parseInt(numberOfHazardousComponents);
    }
    if (batteryTechnologyType !== "Does Not Contain a Battery") {
      MPItem.Orderable.batterySize = batterySize;
      MPItem.Orderable.hasBatteries = hasBatteries;
      if (batteryTechnologyType === "Lithium Ion") {
        MPItem.Orderable.lithiumIonBatteries = {
          ionBatteryModel: batteryModel,
          ionBatteryFormFactor: batteryFormFactor,
          ionNumberOfBatteries: parseInt(numberOfBatteries),
          ionNumberOfBatteryCells: parseInt(numberOfBatteryCells),
          ionIncludedBatteryPackaging: includedBatteryPackaging,
          ionBatteryWeight: parseFloat(batteryWeight),
          batteryWattHour: parseFloat(batteryWattHour)
        };
      } else if (batteryTechnologyType === "Lithium Primary (Lithium Metal)") {
        MPItem.Orderable.lithiumMetalBatteries = {
          metalBatteryModel: batteryModel,
          metalBatteryFormFactor: batteryFormFactor,
          metalNumberOfBatteries: parseInt(numberOfBatteries),
          metalBatteryCellCount: parseInt(numberOfBatteryCells),
          metalIncludedBatteryPackaging: includedBatteryPackaging,
          metalBatteryWeight: parseFloat(batteryWeight),
          lithiumMetalContentWeight: parseFloat(lithiumMetalContentWeight)
        };
      }
    }

    // insert metadata
    try {
      const metadata = {
        batchName: "",
        email: user,
        productName: currProduct.title.slice(0, 200),
        productType: productType,
        image: currProduct.image,
        sku: sku.slice(0, 50).trim(),
        gtin: gtin,
        upc: upc,
        cost: cost ? parseFloat(cost) : 0,
        qty: qty ? parseInt(qty) : 0,
        innerPack: caseSetup === "case" ? parseInt(innerPack) : 1,
        source: source,
        note: note,
        expirationDate: expirationDate !== "" ? new Date(expirationDate) : null,
        purchaseDate: purchaseDate !== "" ? new Date(purchaseDate) : null,
        inboundOrderId: "",
        shipmentId: "",
        schema: schema,
        MPItem: MPItem,
        length: length,
        width: width,
        height: height,
        weight: weight,
        additionalCosts: additionalCosts ? parseFloat(additionalCosts) : 0
      }

      const bodyMetadata = {
        metadata: metadata
      }

      fetchWrapperNoAuth(`${SERVER}/falcon_insert_metadata`, user, password, { method: 'POST', body: bodyMetadata });
    } catch (error) {
      console.error('Error insert metadata:', error.message);
    }

    const url = skuUpdate ? `${SERVER}/post_bulk_wfs_item_setup_multiple` : `${SERVER}/send_item_feed_match`;
    const body = skuUpdate ? { MPItem: MPItem } : {
      item: {
        sku: sku.slice(0, 50).trim(),
        upc: upc,
        price: float2Decimal(parseFloat(price)),
        weight: float2Decimal(convertsMass(parseFloat(weight), weightUnit, 'lbs'))
      }
    }

    try {
      let feedId = "";
      if (!props.multiple) {
        const response = await fetchWrapper(url, user, password, encodedAuth, { method: 'POST', body: body });
        feedId = response.feedId;
      }

      const tempAddedItem = {
        ...currProduct,
        availability: "Out_of_stock",
        condition: "New",
        gtin: gtin,
        isDuplicate: false,
        lifecycleStatus: "ACTIVE",
        mart: "WALMART_US",
        price: {
          currency: "USD",
          amount: float2Decimal(price),
        },
        productName: currProduct.title.slice(0, 200),
        productType: currProduct.productType,
        publishedStatus: "INIT",  // can be PUBLISHED / READY_TO_PUBLISH / IN_PROGRESS / UNPUBLISHED / STAGE / SYSTEM_PROBLEM
        shelf: currProduct.shelf,
        sku: sku.trim(),
        unpublishedReasons: {
          reason: []
        },
        upc: upc,
        wpid: "",

        status: "LOADING",        // can be LOADING / SUCCESS / WARNING / ERROR
        feedStatus: "INIT",       // can be RECEIVED / INPROGRESS / PROCESSED / ERROR
        feedId: feedId,
        image: currProduct.image,
        description: currProduct.description,

        qty: parseInt(qty),
        innerPack: caseSetup === "case" ? parseInt(innerPack) : 1,
        itemId: currProduct.itemId,
        cost: cost ? parseFloat(cost) : 0,
        length: float2Decimal(convertsLength(parseFloat(length), dimensionsUnit, "inch")),
        width: float2Decimal(convertsLength(parseFloat(width), dimensionsUnit, "inch")),
        height: float2Decimal(convertsLength(parseFloat(height), dimensionsUnit, "inch")),
        weight: float2Decimal(convertsMass(parseFloat(weight), weightUnit, "lbs")),
        brand: brand.slice(0, 60),
        source: source,
        expirationDate: expirationDate,
        purchaseDate: purchaseDate,
        note: note,

        manufacturer: currProduct.manufacturer,
        images: currProduct.images,
        MPItem: MPItem,
        isWfs: true,
        lastRequest: new Date().toISOString(),
        step: 1,
        additionalCosts: additionalCosts ? parseFloat(additionalCosts) : 0
      }

      setAddedItem(tempAddedItem);

    } catch (error) {
      console.error('Error:', error.message);
      setInProgress(false);
      return;
    }
  };

  const handleAddRestriction = () => {
    const newRestriction = {
      stateRestrictionsText,
      states: stateRestrictionsStates,
      zipCodes: stateRestrictionsZipCodes,
    };

    // Add the new restriction to the array
    setStateRestrictions((prev) => [...prev, newRestriction]);

    // Reset the form inputs
    setStateRestrictionsText("None");
    setStateRestrictionsStates("");
    setStateRestrictionsZipCodes("");
  };

  const handleRemoveRestriction = (index) => {
    setStateRestrictions((prev) => prev.filter((_, i) => i !== index));
  };

  const getSafetyDataSheets = async (upc, name) => {
    const body = {
      upc: upc,
      name: name
    };
    try {
      const data = await fetchWrapperNoAuth(`${SERVER}/get_safety_data_sheets`, user, password, { method: "POST", body: body });
      if (data.length > 0) {
        setSafetyDataSheetOptions(data);
        setOpenSafetyModal(true);
      } else {
        alert("No safety data sheets found");
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleSelectURL = (input) => {
    setSafetyDataSheets((prev) => {
      if (!prev.some((item) => item === buildLink(input))) {
        return [...prev, buildLink(input)];
      }
      return prev;
    });
  };

  const handleAddSafetySheet = (input) => {
    setSafetyDataSheets((prev) => {
      if (!prev.some((item) => item === input)) {
        return [...prev, input];
      }
      return prev;
    });
  };

  const handleSelectImage = (input) => {
    if (!labelImageContains) {
      alertError("Please select 'Label Image Contains' first");
      return;
    }
    if (labelImage.filter(item => item.labelImageContains === labelImageContains && item.labelImageURL === input).length > 0) {
      return;
    }
    setLabelImage((prev) => [...prev, { labelImageContains: labelImageContains, labelImageURL: input }]);
  };

  const handleRemoveURL = (index) => {
    setSafetyDataSheets((prev) => prev.filter((_, i) => i !== index));
  };

  const handleRemoveLabelImage = (index) => {
    setLabelImage((prev) => prev.filter((_, i) => i !== index));
  };

  const handleApplySkuBuiler = () => {
    const filteredInputs = skuInputs.filter(input => input.selected);
    if (filteredInputs.length === 0) {
      alertError("SKU Builder configuration is empty.");
      return;
    }
    const skuParts = [];
    for (let input of filteredInputs) {
      switch (input.name) {
        case "Cost Price":
          if (!cost) {
            alertError("Please fill Cost Price field to generate SKU");
            return;
          }
          skuParts.push(parseFloat(cost).toFixed(2));
          break;
        case "Source":
          if (!source) {
            alertError("Please fill Source field to generate SKU");
            return;
          }
          skuParts.push(source.slice(0, 3));
          break;
        case "Expiration Date":
          if (!expirationDate) {
            skuParts.push("ED");
          } else {
            skuParts.push(expirationDate);
          }
          break;
        case "Purchase Date":
          if (!purchaseDate) {
            skuParts.push("PD");
          } else {
            skuParts.push(purchaseDate);
          }
          break;
        case "Note":
          if (!note) {
            skuParts.push(" ");
          } else {
            skuParts.push(note.slice(0, 5));
          }
          break;
        case "Selling Price":
          if (!price) {
            alertError("Please fill Selling Price field to generate SKU");
            return;
          }
          skuParts.push(parseFloat(price).toFixed(2));
          break;
        case "Profit":
          if (!price || !height || !length || !width || !weight || !cost) {
            alertError("For profit calculation, you need all these fields: 'Selling Price,' 'Height,' 'Length,' 'Width,' 'Weight,' and 'Cost Price' to generate a SKU.");
            return;
          }
          const refferalFeesProfit = calcRefFees({ price: { amount: price }, shelf: currProduct.shelf });
          const wfsCostProfit = calcWfs({
            height: convertsLength(parseFloat(height), dimensionsUnit, "inch"),
            length: convertsLength(parseFloat(length), dimensionsUnit, "inch"),
            width: convertsLength(parseFloat(width), dimensionsUnit, "inch"),
            weight: convertsMass(parseFloat(weight), weightUnit, 'lbs')
          }) + ((price && price < 10) ? 1 : 0);
          const feesPriceProfit = refferalFeesProfit + wfsCostProfit;
          const profitProfit = price - cost - feesPriceProfit;
          skuParts.push(parseFloat(profitProfit).toFixed(2));
          break;
        case "Breakeven":
          if (!price || !height || !length || !width || !weight || !cost) {
            alertError("For profit calculation, you need all these fields: 'Selling Price,' 'Height,' 'Length,' 'Width,' 'Weight,' and 'Cost Price' to generate a SKU.");
            return;
          }
          const refferalFeesBreakeven = calcRefFees({ price: { amount: price }, shelf: currProduct.shelf });
          const wfsCostBreakeven = calcWfs({
            height: convertsLength(parseFloat(height), dimensionsUnit, "inch"),
            length: convertsLength(parseFloat(length), dimensionsUnit, "inch"),
            width: convertsLength(parseFloat(width), dimensionsUnit, "inch"),
            weight: convertsMass(parseFloat(weight), weightUnit, 'lbs')
          }) + ((price && price < 10) ? 1 : 0);
          const feesPriceBreakeven = refferalFeesBreakeven + wfsCostBreakeven;
          const breakeven = price - feesPriceBreakeven;
          skuParts.push(parseFloat(breakeven).toFixed(2));
          break;
        case "Quantity":
          if (!qty) {
            alertError("Please fill Quantity field to generate SKU");
            return;
          }
          skuParts.push(qty);
          break;
        case "Barcode":
          if (!barcode) {
            alertError("Please fill Barcode field to generate SKU");
            return;
          }
          skuParts.push(barcode);
          break;
        case "Item ID":
          if (!currProduct.itemId) {
            skuParts.push("ID");
          } else {
            skuParts.push(currProduct.itemId);
          }
          break;
        case "Brand":
          if (!brand) {
            alertError("Please fill Brand field to generate SKU");
            return;
          }
          skuParts.push(brand);
          break;
        case "Random No.":
          skuParts.push(Math.floor(10000 + Math.random() * 90000));
          break;
        default:
          break;
      }
    }
    const skuBuilder = skuParts.join((skuSeperator && skuSeperator !== "NONE") ? skuSeperator : "");
    if (skuBuilder.length > 50) {
      alertError(<>Your SKU is {skuBuilder.length} characters long, but the limit is 50.<br />Please configure your SKU builder.</>);
      return;
    }

    setSku(skuBuilder);
  };

  const loadSchema = async (productType) => {
    setLoading(true);
    const url = `${SERVER}/post_get_spec`;
    const body = {
      feedType: skuUpdate ? "MP_WFS_ITEM" : "OMNI_WFS",
      productTypes: [productType]
    };

    try {
      const data = await fetchWrapper(url, user, password, encodedAuth, { method: 'POST', body: body });
      setSchema(skuUpdate ? data.schema.properties?.MPItem?.items?.properties?.Visible : data.schema.properties?.SupplierItem?.items?.properties?.Visible);
      // setNotification({ open: true, message: 'Schema loaded successfully!' });
    } catch (err) {
      console.error('Error loading schema:', err);
      alertError(`Failed to load schema: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // const handleDecimalChange = (e, setValue) => {
  //   let value = e.target.value.replace(/\D/g, "");
  //   if (value) {
  //     value = (parseInt(value, 10) / 100).toFixed(2);
  //   }
  //   setValue(value);
  // };

  const updateFormField = (setter) => (value) => setter(value);

  // Validation pipeline
  const processItem = async (currentItem) => {
    // Skip empty items
    if (!currentItem || Object.keys(currentItem).length === 0) {
      return null;
    }

    // If item is in TIME_BRAKE status and last request was less than 1 minute ago, skip processing
    if (currentItem.status === "TIME_BRAKE") {
      const oneMinuteMs = 1 * 60 * 1000;
      const lastRequestTime = new Date(currentItem.lastRequest).getTime();
      const elapsed = Date.now() - lastRequestTime;

      if (elapsed < oneMinuteMs) {
        return currentItem; // Not time to retry yet, return unchanged
      }
    }

    let updatedItem;

    if (skuUpdate) {
      if (currentItem.step === 1) {          // Check feed status
        updatedItem = await checkFeed(currentItem, user, password, "wfs", "MobileForm.js", encodedAuth);
      } else if (currentItem.step === 2) {   // Check published status
        updatedItem = await checkItem(currentItem, user, password, "wfs", "MobileForm.js", encodedAuth);
      } else if (currentItem.step === 3) {
        updatedItem = {
          ...currentItem,
          status: (currentItem.publishedStatus === "PUBLISHED" &&
            currentItem.feedStatus !== "ERROR") ? "SUCCESS" : "ERROR",
          stepRetryCount: 0 // Reset for future use
        };
      } else {
        updatedItem = currentItem;
      }
    } else {
      if (currentItem.step === 1) {          // Check feed status
        updatedItem = await checkFeed(currentItem, user, password, "wfs", "MobileForm.js", encodedAuth);
      } else if (currentItem.step === 2) {   // Check published status
        updatedItem = await checkItem(currentItem, user, password, "wfs", "MobileForm.js", encodedAuth);
      } else if (currentItem.step === 3) {   // Convert item to WFS
        updatedItem = await convertItemWFS(currentItem, user, password, "wfs", "MobileForm.js", encodedAuth);
      } else if (currentItem.step === 4) {   // Check feed status after WFS conversion
        updatedItem = await checkFeed(currentItem, user, password, "wfs", "MobileForm.js", encodedAuth);
      } else if (currentItem.step === 5) {   // Check published status after WFS conversion
        updatedItem = await checkItem(currentItem, user, password, "wfs", "MobileForm.js", encodedAuth);
      } else if (currentItem.step === 6) {
        updatedItem = {
          ...currentItem,
          status: (currentItem.publishedStatus === "PUBLISHED" &&
            currentItem.feedStatus !== "ERROR") ? "SUCCESS" : "ERROR",
          stepRetryCount: 0 // Reset for future use
        };
      } else {
        updatedItem = currentItem;
      }
    }

    return updatedItem;
  };

  // Cleaner useEffect with interval
  useEffect(() => {
    // Only start interval when in appropriate states and item is not empty
    if (!addedItem || Object.keys(addedItem).length === 0 ||
      !["LOADING", "TIME_BRAKE"].includes(addedItem.status)) {
      return;
    }

    // Update the ref with the current addedItem
    currentItemRef.current = addedItem;

    const interval = setInterval(async () => {
      try {
        const currentItem = currentItemRef.current;

        // Process the current item
        const updatedItem = await processItem(currentItem);

        // If no changes were made (e.g., still in TIME_BRAKE waiting period), just return
        if (!updatedItem || updatedItem === currentItem) {
          return;
        }

        // Update the ref with new state
        currentItemRef.current = updatedItem;

        // Update React state
        setCurrProduct(updatedItem);
        setAddedItem(updatedItem);

        // If we're done, stop the interval
        if (updatedItem.status === 'SUCCESS' || updatedItem.status === 'ERROR' || updatedItem.status === 'WARNING') {
          setInProgress(false);
          clearInterval(interval);
        }
      } catch (error) {
        console.error("Error in process interval:", error);
        const currentItem = currentItemRef.current;

        // Implement retry logic
        const stepRetryCount = (currentItem.stepRetryCount || 0) + 1;

        // If we haven't reached max retries for this step, continue without error state
        if (stepRetryCount < MAX_STEP_RETRIES) {
          console.log(`Retrying step ${currentItem.step} for item ${currentItem.sku.trim()} (attempt ${stepRetryCount} of ${MAX_STEP_RETRIES})`);
          const retryItem = {
            ...currentItem,
            stepRetryCount,
            lastRequest: new Date().toISOString()
          };

          // Update both ref and state
          currentItemRef.current = retryItem;
          setCurrProduct(retryItem);
          setAddedItem(retryItem);
          return; // Continue the interval to retry
        }

        // If we've reached max retries, send error message and update status
        sendErrorMessage(user, password, "wfs", currentItem.itemId, currentItem.upc, "MobileForm.js", currentItem.step, "processItem", "ERROR", [error.toString()]);

        const errorItem = {
          ...currentItem,
          status: 'ERROR',
          lastRequest: new Date().toISOString(),
          unpublishedReasons: {
            reason: [...(currentItem.unpublishedReasons?.reason || []),
            `Unexpected error after ${MAX_STEP_RETRIES} retries: ${error.message}`]
          },
          stepRetryCount: 0 // Reset for future use
        };

        // Update both ref and state
        currentItemRef.current = errorItem;
        setCurrProduct(errorItem);
        setAddedItem(errorItem);
        setInProgress(false);
        clearInterval(interval);
      }
    }, 10000);

    // Clear on unmount
    return () => clearInterval(interval);
  }, [addedItem]);

  useEffect(() => {
    if (chemicalAerosolPesticide === "Yes" && safetyDataSheetOptions.length === 0) {
      getSafetyDataSheets(barcode, currProduct.title || currProduct.productName);
    }
  }, [chemicalAerosolPesticide]);

  useEffect(() => {
    if (batteryTechnologyType !== "Does Not Contain a Battery") {
      setChemicalAerosolPesticide("Yes");
      setHasBatteries("Yes");
    } else {
      setChemicalAerosolPesticide("No");
      setHasBatteries("No");
    }
  }, [batteryTechnologyType]);

  useEffect(() => {
    setCurrProduct(prev => ({
      ...prev,
      productType: productType
    }))
  }, [productType])

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const itemId = searchParams.get("itemId");

    const getCurrProductData = async (itemId) => {
      setIsFetching(true);
      let updatedItem = {};
      try {
        const items = await fetchWrapper(`${SERVER}/get_item_search`, user, password, encodedAuth, { method: 'POST', body: { query: itemId } });
        updatedItem = items[0];
      } catch (error) {
        console.error('Error:', error);
      }

      try {
        let nextData = await fetchNextDataWithRetry(user, password, itemId);
        if ((!nextData.length && !nextData.height && !nextData.width) || !nextData.weight || !nextData.manufacturer) {
          try {
            const azData = await fetchWrapperNoAuth(`${SERVER}/get_item_amazon_data/${updatedItem.upc || nextData.upc}`, user, password);
            if (azData.fromAz) {
              if ((!nextData.length && !nextData.height && !nextData.width) && (azData.length && azData.height && azData.width)) {
                nextData.dimsFromAz = true;
                nextData.length = azData.length;
                nextData.height = azData.height;
                nextData.width = azData.width;
              } else {
                nextData.dimsFromAz = false;
              }
              if (!nextData.weight && azData.weight) {
                nextData.weightFromAz = true;
                nextData.weight = azData.weight;
              } else {
                nextData.weightFromAz = false;
              }
              if (!nextData.manufacturer && azData.manufacturer) {
                nextData.manufacturerFromAz = true;
                nextData.manufacturer = azData.manufacturer;
              } else {
                nextData.manufacturerFromAz = false;
              }
              if (!nextData.brand && azData.brand) {
                nextData.brandFromAz = true;
                nextData.brand = azData.brand;
              } else {
                nextData.brandFromAz = false;
              }
            }
          } catch (error) {
            console.error(`Failed to prefetch data (get_amazon_bb_data) for UPC: ${updatedItem.upc}:`, error.message);
          }
        }
        updatedItem = mergeObjects(updatedItem, nextData);
      } catch (error) {
        console.error(`Failed to prefetch data (get_item_nextdata) for Item Id: ${updatedItem.itemId}:`, error.message);
      }

      try {
        const catalogSearch = await fetchWrapper(`${SERVER}/post_catalog_search/${itemId}/${user}`, user, password, encodedAuth);
        updatedItem = mergeObjects(updatedItem, catalogSearch.dbItems[0] || {});
        updatedItem = mergeObjects(updatedItem, catalogSearch.ctItems[0] || {});
      } catch (error) {
        console.error(`Failed to fetch from post_catalog_search for Item Id: ${itemId}:`, error.message);
      }

      if (updatedItem) {
        const isProductTypeIncluded = PRODUCT_TYPES.map(product => product.name).includes(updatedItem.productType ?? "");
        setProductType(isProductTypeIncluded ? updatedItem.productType : "");
        setPrice(updatedItem.price?.amount || "");
        setWeight(updatedItem.weight || "");
        setLength(updatedItem.length || "");
        setWidth(updatedItem.width || "");
        setHeight(updatedItem.height || "");
        setSku(updatedItem.sku || "");
        setSkuFound((updatedItem.sku || "") !== "");
        setBrand(updatedItem.brand || "");
        setBarcode(updatedItem.upc || "");
        setChangeBarcode(!((updatedItem.upc || "").length > 12))
        setQty(updatedItem.qty || "");
        setCost(updatedItem.cost || "");
        setExpirationDate(updatedItem?.expiration_date ? new Date(updatedItem?.expiration_date).toLocaleDateString() : "");
        setPurchaseDate(updatedItem?.purchase_date ? new Date(updatedItem?.purchase_date).toLocaleDateString() : new Date().toLocaleDateString());
        setNote(updatedItem.note || "");
        setAdditionalCosts(updatedItem.additionalCosts || "");
        setSchema(Object.keys(updatedItem?.schema || {}).includes('$schema') ? updatedItem?.schema : null);
        setFormData(updatedItem?.MPItem?.Visible || updatedItem?.mp_item?.Visible || {});
        // if (!(updatedItem?.schema ?? null)) {
        loadSchema(updatedItem.productType);
        // }
        setCurrProduct(updatedItem);
      }
      setIsFetching(false);
    }

    if (itemId) {
      getCurrProductData(itemId);
    }

  }, [location.search]);

  const styles = {
    container: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: '-webkit-fill-available',
      overflowY: 'auto',
      backgroundColor: theme.background.primary,
      border: `2px solid ${theme.border.medium}`,
      borderRadius: '10px',
      m: '10px',
      p: 2,
      '&::-webkit-scrollbar': {
        width: '10px', // Width of the scrollbar
        height: '10px', // Width of the scrollbar
      },
      '&::-webkit-scrollbar-track': {
        backgroundColor: theme.background.primary, // Track color
        borderRadius: '15px', // Optional: rounded corners
        border: `1px solid ${theme.border.light}`,
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: theme.scrollbar, // Thumb color
        borderRadius: '15px', // Optional: rounded corners
        cursor: 'grab',
      },
      '&::-webkit-scrollbar-thumb:hover': {
        backgroundColor: theme.scrollbarHover, // Thumb color on hover
      },
      '&::-webkit-scrollbar-button': {
        display: 'none', // Thumb color on hover
      },
    },
    gridContainer: {
      color: theme.text.primary,
      mb: 2,
      minWidth: '100%'
    },
    content: {
      width: "100%",
      margin: "auto",
      mt: 3,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
    },
    helperText: {
      fontFamily: theme.fontFamily,
      color: theme.text.tertiary,
      fontSize: 12
    },
    button: {
      width: '100%',
      mb: 1,
      textTransform: 'none',
      fontFamily: theme.fontFamily,
      border: `1px solid ${theme.border.light}`,
      borderRadius: '10px',
      color: theme.text.negative,
      backgroundColor: theme.button.background,
    },
    profitCard: {
      border: `1px solid ${theme.border.medium}`,
      bgcolor: theme.background.secondary,
      height: 'auto',
      mb: 2
    },
    profitItem: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      p: 1,
      borderRadius: 1,
    },
    cardHeader: {
      pb: 0,
      fontFamily: theme.fontFamily,
      fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem', lg: '1.2rem', xl: '1.2rem' },
      fontWeight: 'bold',
      color: theme.text.primary
    },
    profitAnalysisText: {
      fontFamily: theme.fontFamily,
      fontWeight: 'medium',
      color: theme.text.primary,
      fontSize: {
        xs: "0.8rem",
        sm: "0.875rem",
        md: "0.9rem",
        lg: "0.9rem",
        xl: "0.9rem",
      },
    },
    profitAnalysisData: {
      fontFamily: theme.fontFamily,
      fontWeight: 'bold',
      fontSize: {
        xs: "0.8rem",
        sm: "0.875rem",
        md: "0.9rem",
        lg: "0.9rem",
        xl: "0.9rem",
      },
    },
    sectionTitle: {
      fontFamily: theme.fontFamily,
      mb: 1,
      fontSize: {
        xs: "0.9rem",
        sm: "1rem",
        md: "1.15rem",
        lg: "1.15rem",
        xl: "1.15rem",
      },
    }
  };

  if (isFetching) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CustomCircularProgress />
      </Box>
    )
  }

  return (
    <Box sx={styles.container}>
      {inProgress ? (
        <CustomStepProgressMobile step={addedItem.step} totalSteps={skuUpdate ? 3 : 6} />
      ) : (
        <>
          {addedItem.status === 'ERROR' ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <ErrorIcon color="error" sx={{ fontSize: 64, mb: 2 }} />
              <Typography variant="h5" sx={{ textAlign: 'center', fontFamily: theme.fontFamily, color: theme.accent.error }}>
                Error Processing Item
              </Typography>
              <Typography variant="body1" sx={{ textAlign: 'center', fontFamily: theme.fontFamily, color: theme.text.primary }}>
                There was an error processing your item. Please try again or contact support.
              </Typography>
            </Box>
          ) : addedItem.status === 'WARNING' ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <WarningIcon color="warning" sx={{ fontSize: 64, mb: 2 }} />
              <Typography variant="h5" sx={{ textAlign: 'center', fontFamily: theme.fontFamily, color: theme.accent.warning }}>
                Item Not Published
              </Typography>
              {addedItem?.unpublishedReasons?.reason && addedItem.unpublishedReasons.reason.length > 0 && (
                <Box sx={{ mt: 2, mb: 2, width: '100%', maxWidth: '400px', px: 2 }}>
                  {addedItem.unpublishedReasons.reason.map((reason, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        mb: 1,
                        p: 1.5,
                        backgroundColor: theme.background?.secondary || 'rgba(255, 193, 7, 0.1)',
                        borderRadius: 1,
                        borderLeft: `3px solid ${theme.accent.warning}`
                      }}
                    >
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          backgroundColor: theme.accent.warning,
                          mt: 0.75,
                          mr: 1.5,
                          flexShrink: 0
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          color: theme.text.primary,
                          fontFamily: theme.fontFamily,
                          lineHeight: 1.4
                        }}
                      >
                        {reason}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
              <Typography variant="body1" sx={{ textAlign: 'center', fontFamily: theme.fontFamily, color: theme.text.primary }}>
                Your item encountered some issues during publishing.
              </Typography>
            </Box>
          ) : addedItem.status === 'SUCCESS' ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <CheckCircleIcon color="success" sx={{ fontSize: 64, mb: 2 }} />
              <Typography variant="h5" sx={{ textAlign: 'center', fontFamily: theme.fontFamily, color: theme.accent.success }}>
                Processing Completed Successfully
              </Typography>
              <Typography variant="body1" sx={{ textAlign: 'center', fontFamily: theme.fontFamily, color: theme.text.primary }}>
                Your item has been successfully added to your inventory. you can go back to Marter now.
              </Typography>
            </Box>
          ) : (
            <Box sx={styles.content}>
              <Grid container sx={styles.gridContainer} spacing={2}>
                <Grid size={12}>
                  <Typography variant="h5" sx={{ fontFamily: theme.fontFamily, textAlign: 'center', fontWeight: 'bold', }}>
                    Add single item to inventory
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 6 }}>
                  {/* Product Preview Section */}
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      border: `1px solid ${theme.border.medium}`,
                      bgcolor: theme.background.secondary
                    }}
                  >
                    <Box sx={{ display: "flex", gap: 2, }}>
                      <img
                        src={currProduct.image}
                        alt={currProduct.title}
                        style={{
                          width: '70px',
                          height: '70px',
                          objectFit: 'contain',
                          borderRadius: '8px',
                          border: `2px solid ${theme.border.light}`,
                          cursor: 'pointer',
                        }}
                        onError={({ currentTarget }) => {
                          currentTarget.onerror = null;
                          currentTarget.src = "https://i5.walmartimages.com/seo/Another-Product-Image.jpeg";
                        }}
                        onClick={() => window.open(`https://www.walmart.com/ip/${currProduct.itemId}`)}
                      />
                      <Box sx={{ flex: 1, color: theme.text.secondary, }}>
                        <Typography variant="body2" sx={{ fontFamily: theme.fontFamily, mb: 1, color: theme.text.primary }}>
                          {currProduct.title}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ gap: 2, }}>
                      {currProduct.brand ? (
                        <Typography variant="body2" sx={{ mb: 0.5, fontFamily: theme.fontFamily, color: theme.text.primary }}>
                          Brand: {brand}
                        </Typography>
                      ) : (
                        <CustomInput
                          size="small"
                          label={"Brand"}
                          value={brand}
                          onChange={(e) => setBrand(e.target.value)}
                          type="text"
                          inputProps={{
                            minLength: 1,
                            maxLength: 60,
                          }}
                          isError={!brand || brand.length < 1 || brand.length > 60}
                          helperText={
                            <Typography sx={styles.helperText}>1-60 characters<InfoOutlinedIcon sx={{ fontSize: '16px', }} /></Typography>
                          }
                        />
                      )}

                      <Typography variant="body2" sx={{ fontFamily: theme.fontFamily, color: theme.text.primary }}>
                        Buy Box Price: ${parseFloat(currProduct.price?.amount).toFixed(2)}
                      </Typography>

                      <Box sx={{ display: 'flex', alignItems: 'center', }}>
                        <Typography variant="body2" sx={{ fontFamily: theme.fontFamily, mr: '10px', color: theme.text.primary }}>
                          Item ID: {currProduct.itemId}
                        </Typography>
                        <Typography sx={{ fontFamily: theme.fontFamily, textDecoration: 'underline', fontSize: 12, cursor: 'pointer', color: theme.text.primary }} onClick={() => window.open(`https://www.walmart.com/ip/${currProduct.itemId}`)}>
                          open in walmart.com
                        </Typography>
                      </Box>

                      {changeBarcode ? (
                        <Typography variant="body2" sx={{ mb: 0.5, fontFamily: theme.fontFamily, color: theme.text.primary }}>
                          Barcode: {barcode} <IconButton onClick={() => setChangeBarcode(!changeBarcode)}><ChangeCircleOutlinedIcon sx={{ color: theme.text.primary, }} /></IconButton>
                        </Typography>
                      ) : (
                        <CustomInput
                          size="small"
                          label={"Barcode"}
                          value={barcode}
                          onChange={(e) => setBarcode(e.target.value)}
                          type="text"
                          inputProps={{
                            minLength: 12,
                            maxLength: 14,
                          }}
                          isError={!barcode || barcode.length < 12 || barcode.length > 14}
                          helperText={
                            <Typography sx={styles.helperText}>12-14 characters<InfoOutlinedIcon sx={{ fontSize: '16px', }} /></Typography>
                          }
                        />
                      )}
                    </Box>
                  </Paper>
                </Grid>

                <Grid item size={{ xs: 12, sm: 12, md: 6, lg: 4, xl: 3 }}>
                  <Accordion elevation={3} sx={styles.profitCard}>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon sx={{ color: theme.text.primary, }} />}
                      aria-controls="profit-analysis-content"
                      id="profit-analysis-header"
                    >
                      <Typography variant="h6" sx={styles.cardHeader}>Profit Analysis</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      {(() => {
                        const refferalFees = calcRefFees({ price: { amount: price }, shelf: currProduct.shelf });
                        const wfsCost = calcWfs({
                          height: convertsLength(parseFloat(height), dimensionsUnit, "inch"),
                          length: convertsLength(parseFloat(length), dimensionsUnit, "inch"),
                          width: convertsLength(parseFloat(width), dimensionsUnit, "inch"),
                          weight: convertsMass(parseFloat(weight), weightUnit, 'lbs')
                        }) + ((price && price < 10) ? 1 : 0);
                        const feesPrice = refferalFees + wfsCost;
                        const profit = price - cost - feesPrice - (parseFloat(additionalCosts) || 0);
                        const roi = (profit / cost * 100);

                        return (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            <Box sx={{ ...styles.profitItem, bgcolor: profit > 0 ? `${theme.accent.success}15` : `${theme.accent.error}15`, }}>
                              <Typography variant="body1" sx={styles.profitAnalysisText}>
                                Profit:
                              </Typography>
                              <Typography variant="body1" sx={{ ...styles.profitAnalysisData, color: profit > 0 ? theme.accent.success : theme.accent.error, }}>
                                {profit < 0 && "-"}${profit < 0 ? (profit * -1).toFixed(2) : profit.toFixed(2)}/unit
                              </Typography>
                            </Box>

                            <Box sx={{ ...styles.profitItem, bgcolor: roi > 0 ? `${theme.accent.success}15` : `${theme.accent.error}15`, }}>
                              <Typography variant="body1" sx={styles.profitAnalysisText}>
                                ROI:
                              </Typography>
                              <Typography variant="body1" sx={{ ...styles.profitAnalysisData, color: roi > 0 ? theme.accent.success : theme.accent.error, }}>
                                {roi < 0 && "-"}{roi < 0 ? (roi * -1).toFixed(2) : roi.toFixed(2)}%
                              </Typography>
                            </Box>

                            <Box sx={{ ...styles.profitItem, bgcolor: (profit * qty) > 0 ? `${theme.accent.success}15` : `${theme.accent.error}15`, }}>
                              <Typography variant="body1" sx={styles.profitAnalysisText}>
                                Total Profit:
                              </Typography>
                              <Typography variant="body1" sx={{ ...styles.profitAnalysisData, color: (profit * qty) > 0 ? theme.accent.success : theme.accent.error, }}>
                                {(profit * qty) < 0 && "-"}${(profit * qty) < 0 ? ((profit * qty) * -1).toFixed(2) : (profit * qty).toFixed(2)}
                              </Typography>
                            </Box>
                          </Box>
                        )
                      })()}
                    </AccordionDetails>
                  </Accordion>

                  <WarningInput
                    fullWidth
                    size="small"
                    label={"Note"}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    type="text"
                    multiline={true}
                    rows={2}
                    isWarning={!note}
                  />
                </Grid>

                <Grid item size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 3 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <SourceInput
                      fullWidth
                      size="small"
                      label="Source"
                      sources={sources}
                      value={source}
                      onChange={setSource}
                      isWarning={!source}
                    />

                    <CostPriceInput
                      fullWidth
                      size="small"
                      label={"Cost Price"}
                      value={cost}
                      onChange={(e) => setCost(e.target.value)}
                      type="number"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Typography sx={{ color: (!cost || parseFloat(cost) === 0) ? theme.accent.warning : theme.text.primary, fontFamily: theme.fontFamily }}>
                              $
                            </Typography>
                          </InputAdornment>
                        )
                      }}
                      isWarning={!cost || parseFloat(cost) === 0}
                    />

                    <WarningEvaluateInput
                      fullWidth
                      size="small"
                      label={"Additional Costs"}
                      value={additionalCosts}
                      onChange={(e) => setAdditionalCosts(e.target.value)}
                      onExpressionEvaluated={(result) => {
                        setAdditionalCosts(result);
                      }}
                      type="text"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Typography sx={{ color: !additionalCosts ? theme.accent.warning : theme.text.primary, fontFamily: theme.fontFamily }}>
                              $
                            </Typography>
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <HtmlTooltip
                              customtheme={theme}
                              title={
                                <Typography sx={{ fontFamily: theme.fontFamily, color: theme.text.primary, fontSize: 12 }}>
                                  You can enter math expressions like: 5*0.5+10.2 = 12.700
                                </Typography>
                              }
                              arrow
                              placement="top"
                              slotProps={{
                                popper: {
                                  modifiers: [
                                    {
                                      name: 'offset',
                                      options: {
                                        offset: [0, -10],
                                      },
                                    },
                                  ],
                                },
                              }}
                            >
                              <InfoOutlinedIcon sx={{ color: !additionalCosts ? theme.accent.warning : theme.color.primary, cursor: 'default' }} />
                            </HtmlTooltip>
                          </InputAdornment>
                        )
                      }}
                      isWarning={!additionalCosts}
                    />

                    <WarningInput
                      fullWidth
                      size="small"
                      label="Expiration Date"
                      value={expirationDate}
                      onChange={(e) => {
                        setExpirationDate(e.target.value);
                        if (e.target.value) {
                          setExpirationDateInputType('date');
                        }
                      }}
                      onFocus={() => setExpirationDateInputType('date')}
                      onBlur={() => {
                        if (!expirationDate) {
                          setExpirationDateInputType('text');
                        }
                      }}
                      type={expirationDateInputType}
                      placeholder="mm/dd/yyyy"
                      isWarning={!expirationDate}
                      InputProps={{
                        inputProps: {
                          max: "2030-12-31"
                        }
                      }}
                    />

                    <WarningInput
                      fullWidth
                      size="small"
                      label="Purchase Date"
                      value={purchaseDate}
                      onChange={(e) => {
                        setPurchaseDate(e.target.value);
                        if (e.target.value) {
                          setPurchaseDateInputType('date');
                        }
                      }}
                      onFocus={() => setPurchaseDateInputType('date')}
                      onBlur={() => {
                        if (!purchaseDate) {
                          setPurchaseDateInputType('text');
                        }
                      }}
                      type={purchaseDateInputType}
                      placeholder="mm/dd/yyyy"
                      isWarning={!purchaseDate}
                      InputProps={{
                        inputProps: {
                          max: "2030-12-31"
                        }
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>

              <Grid container sx={styles.gridContainer} spacing={{ xs: 2, sm: 2, md: 3, lg: 3, xl: 3, }} >
                {/* Basic Information */}
                <Grid size={{ xs: 12, sm: 12, md: 6, lg: 4, xl: 3 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Typography variant="h6" sx={styles.sectionTitle}>
                      Basic Information
                    </Typography>

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={skuUpdate}
                          onChange={(e) => setSkuUpdate(e.target.checked)}
                          disabled={loadingAddItem}
                          sx={{
                            color: theme.color.secondary,
                            '&.Mui-checked': {
                              color: theme.color.primary,
                            },
                          }}
                        />
                      }
                      label={<Typography sx={{ fontFamily: theme.fontFamily, color: theme.text.primary }}>SKU Update</Typography>}
                    />

                    <Box>
                      <CustomInput
                        fullWidth
                        size="small"
                        label={"SKU"}
                        value={sku}
                        onChange={(e) => setSku(e.target.value)}
                        type="text"
                        inputProps={{
                          minLength: 1,
                          maxLength: 50,
                        }}
                        isError={!sku || sku.length < 1 || sku.length > 50}
                        helperText={
                          <Typography sx={styles.helperText}>
                            {skuFound ? "This item has a SKU and can't be changed." : "Enter 1-50 characters."}<InfoOutlinedIcon sx={styles.helperText} />
                          </Typography>
                        }
                      />

                      <Typography variant="body2" sx={{ fontFamily: theme.fontFamily, mt: 1 }}>SKU Builder</Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-around', gap: 2 }}>
                        <Button variant="contained" sx={styles.button} onClick={() => handleApplySkuBuiler()}>Apply</Button>
                        <Button variant="contained" sx={styles.button} onClick={() => setOpenSkuBuilderModal(true)}>Configure</Button>
                      </Box>
                    </Box>

                    {!productType && (
                      <ProductTypeSelector
                        loadSchema={loadSchema}
                        setProductType={setProductType}
                        productType={productType}
                        inputValue={productTypeInput}
                        setInputValue={setProductTypeInput}
                        productTypes={PRODUCT_TYPES.map(product => product.name)}
                      />
                    )}
                  </Box>
                </Grid>

                {/* Product Details */}
                <Grid size={{ xs: 12, sm: 12, md: 6, lg: 8, xl: 6 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Typography variant="h6" sx={styles.sectionTitle}>
                      Product Details
                    </Typography>

                    <Grid container spacing={{ xs: 2, sm: 2, md: 3, lg: 3, xl: 3, }} sx={{ color: theme.text.primary }}>
                      <Grid size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 6 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <CustomInput
                            fullWidth
                            size="small"
                            label={"Selling Price"}
                            value={price}
                            onChange={(e) => setPrice(parseFloat(e.target.value))}
                            type="number"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Typography sx={{ color: !price || parseFloat(price) <= 0 ? theme.accent.error : theme.text.primary, fontFamily: theme.fontFamily }}>
                                    $
                                  </Typography>
                                </InputAdornment>
                              )
                            }}
                            inputProps={{
                              min: 0,
                              max: 1000000000000000,
                              step: 0.01
                            }}
                            isError={!price || parseFloat(price) <= 0}
                          />

                          <CustomInput
                            fullWidth
                            size="small"
                            label={"Quantity"}
                            value={qty}
                            onChange={(e) => setQty(e.target.value)}
                            type="number"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Typography sx={{ color: (!qty || parseInt(qty) < 1) ? theme.accent.error : theme.text.primary, fontFamily: theme.fontFamily }}>
                                    #
                                  </Typography>
                                </InputAdornment>
                              )
                            }}
                            inputProps={{
                              min: 1,
                              max: 10000000,
                              step: 1
                            }}
                            isError={!qty || parseInt(qty) < 1}
                          />

                          <SelectComponent
                            value={caseSetup}
                            title={"Master case setup"}
                            onChange={setCaseSetup}
                            options={[
                              { value: "individual", name: "Individual" },
                              { value: "case", name: "Case-packed" }
                            ]}
                            size={"small"}
                          />

                          {caseSetup !== "individual" && (
                            <CustomInput
                              fullWidth
                              size="small"
                              label={"Units Per Case"}
                              value={innerPack}
                              onChange={(e) => setInnerPack(e.target.value)}
                              type="number"
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <Typography sx={{ color: (!innerPack || parseInt(innerPack) === 0) ? theme.accent.error : theme.text.primary, fontFamily: theme.fontFamily }}>
                                      #
                                    </Typography>
                                  </InputAdornment>
                                )
                              }}
                              inputProps={{
                                min: 1,
                                max: 10000000,
                                step: 1
                              }}
                              isError={!innerPack || parseInt(innerPack) < 1}
                            />
                          )}

                        </Box>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 6 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {/* Weight */}
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <CustomInput
                              fullWidth
                              size="small"
                              label={"Weight"}
                              value={weight}
                              onChange={(e) => setWeight(e.target.value)}
                              type="number"
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <HtmlTooltip
                                      customtheme={theme}
                                      title={
                                        <Typography sx={{ fontFamily: theme.fontFamily, color: theme.text.primary, fontSize: 12 }}>
                                          units found on {currProduct.weightUnit || "lbs"}
                                        </Typography>
                                      }
                                      arrow
                                      placement="top"
                                      slotProps={{
                                        popper: {
                                          modifiers: [
                                            {
                                              name: 'offset',
                                              options: {
                                                offset: [0, -10],
                                              },
                                            },
                                          ],
                                        },
                                      }}
                                    >
                                      <Box
                                        sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', }}
                                        id="IFWeightTypography"
                                        aria-controls={openWeight ? 'IFWeightSelect' : undefined}
                                        aria-haspopup="true"
                                        aria-expanded={openWeight ? 'true' : undefined}
                                        onClick={(event) => setAnchorElWeight(event.currentTarget)}
                                      >
                                        <Typography sx={{ color: weight ? theme.text.primary : theme.accent.error, fontFamily: theme.fontFamily, }}>{weightUnit}</Typography>
                                        <ArrowDropDownOutlinedIcon sx={{ color: weight ? theme.text.primary : theme.accent.error, }} />
                                      </Box>
                                      <Menu
                                        id="IFWeightSelect"
                                        anchorEl={anchorElWeight}
                                        open={openWeight}
                                        onClose={() => setAnchorElWeight(null)}
                                        MenuListProps={{
                                          'aria-labelledby': 'IFWeightTypography',
                                        }}
                                      >
                                        <MenuItem key={"lbs"} selected={"lbs" === weightUnit} onClick={() => handleWeightUnitChange("lbs")}>
                                          lbs
                                        </MenuItem>
                                        <MenuItem key={"ounce"} selected={"ounce" === weightUnit} onClick={() => handleWeightUnitChange("ounce")}>
                                          ounce
                                        </MenuItem>
                                        <MenuItem key={"mg"} selected={"mg" === weightUnit} onClick={() => handleWeightUnitChange("mg")}>
                                          mg
                                        </MenuItem>
                                        <MenuItem key={"gram"} selected={"gram" === weightUnit} onClick={() => handleWeightUnitChange("gram")}>
                                          gram
                                        </MenuItem>
                                        <MenuItem key={"kg"} selected={"kg" === weightUnit} onClick={() => handleWeightUnitChange("kg")}>
                                          kg
                                        </MenuItem>
                                        <MenuItem key={"lbs.oz"} selected={"lbs.oz" === weightUnit} onClick={() => handleWeightUnitChange("lbs.oz")}>
                                          lbs.oz
                                        </MenuItem>
                                      </Menu>
                                    </HtmlTooltip>

                                  </InputAdornment>
                                )
                              }}
                              inputProps={{
                                min: 0,
                                max: 100000000000,
                                step: 0.01
                              }}
                              isError={!weight || parseFloat(weight) <= 0}
                            />
                            {currProduct.hasOwnProperty("weightFromAz") && currProduct.weightFromAz && (
                              <HtmlTooltip
                                customtheme={theme}
                                title={
                                  <Typography sx={{ color: theme.text.primary, fontFamily: theme.fontFamily, fontSize: 12 }}>
                                    weight found on amazon.com
                                  </Typography>
                                }
                                arrow
                                placement="top"
                                slotProps={{
                                  popper: {
                                    modifiers: [
                                      {
                                        name: 'offset',
                                        options: {
                                          offset: [0, -10],
                                        },
                                      },
                                    ],
                                  },
                                }}
                              >
                                <InfoOutlinedIcon size={10} sx={{ color: theme.color.primary, cursor: 'pointer', }} />
                              </HtmlTooltip>
                            )}
                          </Box>

                          {/* Length */}
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <CustomInput
                              fullWidth
                              size="small"
                              label={"Length"}
                              value={length}
                              onChange={(e) => setLength(e.target.value)}
                              type="number"
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <HtmlTooltip
                                      customtheme={theme}
                                      title={
                                        <Typography sx={{ fontFamily: theme.fontFamily, color: theme.text.primary, fontSize: 12 }}>
                                          units found on {currProduct.dimensionsUnit || "inch"}
                                        </Typography>
                                      }
                                      arrow
                                      placement="top"
                                      slotProps={{
                                        popper: {
                                          modifiers: [
                                            {
                                              name: 'offset',
                                              options: {
                                                offset: [0, -10],
                                              },
                                            },
                                          ],
                                        },
                                      }}
                                    >
                                      <Box
                                        sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', }}
                                        id="IFLengthTypography"
                                        aria-controls={openLength ? 'IFLengthSelect' : undefined}
                                        aria-haspopup="true"
                                        aria-expanded={openLength ? 'true' : undefined}
                                        onClick={(event) => setAnchorElLength(event.currentTarget)}
                                      >
                                        <Typography sx={{ color: length ? theme.text.primary : theme.accent.error, fontFamily: theme.fontFamily, }}>{dimensionsUnit}</Typography>
                                        <ArrowDropDownOutlinedIcon sx={{ color: length ? theme.text.primary : theme.accent.error, }} />
                                      </Box>
                                      <Menu
                                        id="IFLengthSelect"
                                        anchorEl={anchorElLength}
                                        open={openLength}
                                        onClose={() => setAnchorElLength(null)}
                                        MenuListProps={{
                                          'aria-labelledby': 'IFLengthTypography',
                                        }}
                                      >
                                        <MenuItem key={"inch"} selected={"inch" === dimensionsUnit} onClick={() => handleDimensionsUnitChange("inch")}>
                                          inch
                                        </MenuItem>
                                        <MenuItem key={"mm"} selected={"mm" === dimensionsUnit} onClick={() => handleDimensionsUnitChange("mm")}>
                                          mm
                                        </MenuItem>
                                        <MenuItem key={"cm"} selected={"cm" === dimensionsUnit} onClick={() => handleDimensionsUnitChange("cm")}>
                                          cm
                                        </MenuItem>
                                        <MenuItem key={"meter"} selected={"meter" === dimensionsUnit} onClick={() => handleDimensionsUnitChange("meter")}>
                                          meter
                                        </MenuItem>
                                        <MenuItem key={"feet"} selected={"feet" === dimensionsUnit} onClick={() => handleDimensionsUnitChange("feet")}>
                                          feet
                                        </MenuItem>
                                        <MenuItem key={"yard"} selected={"yard" === dimensionsUnit} onClick={() => handleDimensionsUnitChange("yard")}>
                                          yard
                                        </MenuItem>
                                      </Menu>
                                    </HtmlTooltip>

                                  </InputAdornment>
                                )
                              }}
                              inputProps={{
                                min: 0,
                                max: 1000000000,
                                step: 0.01
                              }}
                              isError={!length || parseFloat(length) <= 0}
                            />
                            {currProduct.hasOwnProperty("dimsFromAz") && currProduct.dimsFromAz && (
                              <HtmlTooltip
                                customtheme={theme}
                                title={
                                  <Typography sx={{ color: theme.text.primary, fontFamily: theme.fontFamily, fontSize: 12 }}>
                                    length found on amazon.com
                                  </Typography>
                                }
                                arrow
                                placement="top"
                                slotProps={{
                                  popper: {
                                    modifiers: [
                                      {
                                        name: 'offset',
                                        options: {
                                          offset: [0, -10],
                                        },
                                      },
                                    ],
                                  },
                                }}
                              >
                                <InfoOutlinedIcon size={10} sx={{ color: theme.color.primary, cursor: 'pointer', }} />
                              </HtmlTooltip>
                            )}
                          </Box>

                          {/* Width */}
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <CustomInput
                              fullWidth
                              size="small"
                              label={"Width"}
                              value={width}
                              onChange={(e) => setWidth(e.target.value)}
                              type="number"
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <HtmlTooltip
                                      customtheme={theme}
                                      title={
                                        <Typography sx={{ fontFamily: theme.fontFamily, color: theme.text.primary, fontSize: 12 }}>
                                          units found on {currProduct.dimensionsUnit || "inch"}
                                        </Typography>
                                      }
                                      arrow
                                      placement="top"
                                      slotProps={{
                                        popper: {
                                          modifiers: [
                                            {
                                              name: 'offset',
                                              options: {
                                                offset: [0, -10],
                                              },
                                            },
                                          ],
                                        },
                                      }}
                                    >
                                      <Box
                                        sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', }}
                                        id="IFWidthTypography"
                                        aria-controls={openWidth ? 'IFWidthSelect' : undefined}
                                        aria-haspopup="true"
                                        aria-expanded={openWidth ? 'true' : undefined}
                                        onClick={(event) => setAnchorElWidth(event.currentTarget)}
                                      >
                                        <Typography sx={{ color: width ? theme.text.primary : theme.accent.error, fontFamily: theme.fontFamily, }}>{dimensionsUnit}</Typography>
                                        <ArrowDropDownOutlinedIcon sx={{ color: width ? theme.text.primary : theme.accent.error, }} />
                                      </Box>
                                      <Menu
                                        id="IFWidthSelect"
                                        anchorEl={anchorElWidth}
                                        open={openWidth}
                                        onClose={() => setAnchorElWidth(null)}
                                        MenuListProps={{
                                          'aria-labelledby': 'IFWidthTypography',
                                        }}
                                      >
                                        <MenuItem key={"inch"} selected={"inch" === dimensionsUnit} onClick={() => handleDimensionsUnitChange("inch")}>
                                          inch
                                        </MenuItem>
                                        <MenuItem key={"mm"} selected={"mm" === dimensionsUnit} onClick={() => handleDimensionsUnitChange("mm")}>
                                          mm
                                        </MenuItem>
                                        <MenuItem key={"cm"} selected={"cm" === dimensionsUnit} onClick={() => handleDimensionsUnitChange("cm")}>
                                          cm
                                        </MenuItem>
                                        <MenuItem key={"meter"} selected={"meter" === dimensionsUnit} onClick={() => handleDimensionsUnitChange("meter")}>
                                          meter
                                        </MenuItem>
                                        <MenuItem key={"feet"} selected={"feet" === dimensionsUnit} onClick={() => handleDimensionsUnitChange("feet")}>
                                          feet
                                        </MenuItem>
                                        <MenuItem key={"yard"} selected={"yard" === dimensionsUnit} onClick={() => handleDimensionsUnitChange("yard")}>
                                          yard
                                        </MenuItem>
                                      </Menu>
                                    </HtmlTooltip>

                                  </InputAdornment>
                                )
                              }}
                              inputProps={{
                                min: 0,
                                max: 1000000000,
                                step: 0.01
                              }}
                              isError={!width || parseFloat(width) <= 0}
                            />
                            {currProduct.hasOwnProperty("dimsFromAz") && currProduct.dimsFromAz && (
                              <HtmlTooltip
                                customtheme={theme}
                                title={
                                  <Typography sx={{ color: theme.text.primary, fontFamily: theme.fontFamily, fontSize: 12 }}>
                                    width found on amazon.com
                                  </Typography>
                                }
                                arrow
                                placement="top"
                                slotProps={{
                                  popper: {
                                    modifiers: [
                                      {
                                        name: 'offset',
                                        options: {
                                          offset: [0, -10],
                                        },
                                      },
                                    ],
                                  },
                                }}
                              >
                                <InfoOutlinedIcon size={10} sx={{ color: theme.color.primary, cursor: 'pointer', }} />
                              </HtmlTooltip>
                            )}
                          </Box>

                          {/* Height */}
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <CustomInput
                              fullWidth
                              size="small"
                              label={"Height"}
                              value={height}
                              onChange={(e) => setHeight(e.target.value)}
                              type="number"
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <HtmlTooltip
                                      customtheme={theme}
                                      title={
                                        <Typography sx={{ fontFamily: theme.fontFamily, color: theme.text.primary, fontSize: 12 }}>
                                          units found on {currProduct.dimensionsUnit || "inch"}
                                        </Typography>
                                      }
                                      arrow
                                      placement="top"
                                      slotProps={{
                                        popper: {
                                          modifiers: [
                                            {
                                              name: 'offset',
                                              options: {
                                                offset: [0, -10],
                                              },
                                            },
                                          ],
                                        },
                                      }}
                                    >
                                      <Box
                                        sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', }}
                                        id="IFHeightTypography"
                                        aria-controls={openHeight ? 'IFHeightSelect' : undefined}
                                        aria-haspopup="true"
                                        aria-expanded={openHeight ? 'true' : undefined}
                                        onClick={(event) => setAnchorElHeight(event.currentTarget)}
                                      >
                                        <Typography sx={{ color: height ? theme.text.primary : theme.accent.error, fontFamily: theme.fontFamily, }}>{dimensionsUnit}</Typography>
                                        <ArrowDropDownOutlinedIcon sx={{ color: height ? theme.text.primary : theme.accent.error, }} />
                                      </Box>
                                      <Menu
                                        id="IFHeightSelect"
                                        anchorEl={anchorElHeight}
                                        open={openHeight}
                                        onClose={() => setAnchorElHeight(null)}
                                        MenuListProps={{
                                          'aria-labelledby': 'IFHeightTypography',
                                        }}
                                      >
                                        <MenuItem key={"inch"} selected={"inch" === dimensionsUnit} onClick={() => handleDimensionsUnitChange("inch")}>
                                          inch
                                        </MenuItem>
                                        <MenuItem key={"mm"} selected={"mm" === dimensionsUnit} onClick={() => handleDimensionsUnitChange("mm")}>
                                          mm
                                        </MenuItem>
                                        <MenuItem key={"cm"} selected={"cm" === dimensionsUnit} onClick={() => handleDimensionsUnitChange("cm")}>
                                          cm
                                        </MenuItem>
                                        <MenuItem key={"meter"} selected={"meter" === dimensionsUnit} onClick={() => handleDimensionsUnitChange("meter")}>
                                          meter
                                        </MenuItem>
                                        <MenuItem key={"feet"} selected={"feet" === dimensionsUnit} onClick={() => handleDimensionsUnitChange("feet")}>
                                          feet
                                        </MenuItem>
                                        <MenuItem key={"yard"} selected={"yard" === dimensionsUnit} onClick={() => handleDimensionsUnitChange("yard")}>
                                          yard
                                        </MenuItem>
                                      </Menu>
                                    </HtmlTooltip>

                                  </InputAdornment>
                                )
                              }}
                              inputProps={{
                                min: 0,
                                max: 1000000000,
                                step: 0.01
                              }}
                              isError={!height || parseFloat(height) <= 0}
                            />
                            {currProduct.hasOwnProperty("dimsFromAz") && currProduct.dimsFromAz && (
                              <HtmlTooltip
                                customtheme={theme}
                                title={
                                  <Typography sx={{ color: theme.text.primary, fontFamily: theme.fontFamily, fontSize: 12 }}>
                                    height found on amazon.com
                                  </Typography>
                                }
                                arrow
                                placement="top"
                                slotProps={{
                                  popper: {
                                    modifiers: [
                                      {
                                        name: 'offset',
                                        options: {
                                          offset: [0, -10],
                                        },
                                      },
                                    ],
                                  },
                                }}
                              >
                                <InfoOutlinedIcon size={10} sx={{ color: theme.color.primary, cursor: 'pointer', }} />
                              </HtmlTooltip>
                            )}
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>

                {/* Trade Item Configuration */}
                <Grid size={{ xs: 12, sm: 12, md: 12, lg: 4, xl: 3 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Typography variant="h6" sx={styles.sectionTitle}>
                      Trade Item Configuration *
                    </Typography>

                    <SelectCountries
                      value={countryOfOrigin}
                      title={"Country of Origin"}
                      onChange={setCountryOfOrigin}
                      options={COUNTRIES_OF_ORIGIN}
                      size={"small"}
                    />

                    {chemicalAerosolPesticide === "Yes" && (
                      <>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', }}>
                          <SelectComponent
                            value={labelImageContains}
                            title={"Label Image Contains"}
                            onChange={setLabelImageContains}
                            options={[
                              { value: "OTC Dosage / Instructions", name: "OTC Dosage / Instructions" },
                              { value: "Nutrition Facts", name: "Nutrition Facts" },
                              { value: "Ingredient List", name: "Ingredient List" },
                              { value: "Supplement Facts", name: "Supplement Facts" },
                              { value: "Drug Facts", name: "Drug Facts" },
                              { value: "No Label", name: "No Label" },
                            ]}
                            size={"small"}
                            required={true}
                          />
                          <Button
                            onClick={() => setOpenImgModal(true)}
                            endIcon={<AddIcon sx={{ bgcolor: theme.text.negative, color: theme.accent.error, borderRadius: '50%' }} />}
                            sx={{
                              textTransform: 'none',
                              fontFamily: theme.fontFamily,
                              border: `1px solid ${theme.border.light}`,
                              borderRadius: '10px',
                              color: theme.text.negative,
                              backgroundColor: theme.accent.error,
                              width: '50%'
                            }}
                          >
                            Choose
                          </Button>
                        </Box>
                        <CustomInput
                          fullWidth
                          size="small"
                          label={"Label Image URL"}
                          value={labelImageURL}
                          onChange={(e) => setLabelImageURL(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') { handleSelectImage(e.target.value); } }}
                          type="text"
                          inputProps={{
                            minLength: 1,
                            maxLength: 2500,
                          }}
                          isError={labelImage.length === 0 && (!labelImageURL || labelImageURL.length < 1 || labelImageURL.length > 2500)}
                        />
                        {labelImage.length > 0 && labelImage.map((obj, index) => (
                          <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-start', alignItems: 'center' }}>
                              <img
                                id={`labelImage-${index}`}
                                src={obj.labelImageURL}
                                alt={obj.labelImageContains}
                                style={{
                                  width: '50px',
                                  height: 'auto',
                                  maxHeight: '70px',
                                  objectFit: 'contain',
                                  borderRadius: '8px',
                                  border: `1px solid ${theme.border.light}`,
                                  cursor: 'pointer',
                                }}
                                onError={({ currentTarget }) => {
                                  currentTarget.onerror = null;
                                  currentTarget.src = "https://i5.walmartimages.com/seo/Another-Product-Image.jpeg";
                                }}
                                onMouseEnter={() => {
                                  const img = document.getElementById(`labelImage-${index}`);
                                  img.style.transform = "scale(1.5)";
                                  img.style.boxShadow = "1px 1px 15px 1px gray";
                                  img.style.backgroundColor = "white";
                                  img.style.cursor = "pointer";
                                }}
                                onMouseLeave={() => {
                                  const img = document.getElementById(`labelImage-${index}`);
                                  img.style.transform = "none";
                                  img.style.boxShadow = "none";
                                  img.style.backgroundColor = "none";
                                  img.style.cursor = "none";
                                }}
                              />
                              <Typography>{obj.labelImageContains}</Typography>
                            </Box>
                            <IconButton
                              onClick={() => handleRemoveLabelImage(index)}
                              sx={{ color: theme.accent.error }} // Set color for the IconButton
                            >
                              <CloseIcon />
                            </IconButton>
                          </Box>
                        ))}
                      </>
                    )}

                    {labelImage.findIndex(item => item.labelImageContains === "Drug Facts") !== -1 && (
                      <SelectComponent
                        value={requiredStorageCondition}
                        title={"Required Storage Condition"}
                        onChange={setRequiredStorageCondition}
                        options={[
                          { value: "Cool & Dry Place Storage", name: "Cool & Dry Place Storage" },
                          { value: "Refrigerated Storage", name: "Refrigerated Storage" },
                          { value: "Ambient Storage", name: "Ambient Storage" },
                          { value: "Freezer Storage", name: "Freezer Storage" },
                        ]}
                        size={"small"}
                        required={true}
                      />
                    )}
                  </Box>
                </Grid>

                {/* Product Specifications */}
                <Grid size={12}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Typography variant="h6" sx={styles.sectionTitle}>
                      Product Specifications
                    </Typography>

                    <Grid container spacing={2} sx={{ color: theme.text.primary }}>
                      <Grid size={{ xs: 12, sm: 12, md: 6, lg: 4, xl: 3 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <SelectComponent
                            value={electronicsIndicator}
                            title={"Contains Electronic Component?"}
                            onChange={setElectronicsIndicator}
                            options={[
                              { value: "Yes", name: "Yes" },
                              { value: "No", name: "No" }
                            ]}
                            size={"small"}
                          />

                          <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', }}>
                            <SelectComponent
                              value={chemicalAerosolPesticide}
                              title={"Contains Chemical, Aerosol or Pesticide?"}
                              onChange={setChemicalAerosolPesticide}
                              options={[
                                { value: "Yes", name: "Yes" },
                                { value: "No", name: "No" }
                              ]}
                              size={"small"}
                            />
                            {(chemicalAerosolPesticide === "Yes" || ["Lithium Ion", "Lithium Primary (Lithium Metal)", "Lead Acid", "Lead Acid (Non-Spillable)"].includes(batteryTechnologyType)) && (
                              <Button
                                onClick={() => setOpenSafetyModal(true)}
                                endIcon={<AddIcon sx={{ bgcolor: theme.text.negative, color: theme.accent.error, borderRadius: '50%' }} />}
                                sx={{
                                  textTransform: 'none',
                                  fontFamily: theme.fontFamily,
                                  border: `1px solid ${theme.border.light}`,
                                  borderRadius: '10px',
                                  color: theme.text.negative,
                                  backgroundColor: theme.accent.error,
                                  width: '50%'
                                }}
                              >
                                Choose
                              </Button>
                            )}
                          </Box>

                          {(chemicalAerosolPesticide === "Yes" || ["Lithium Ion", "Lithium Primary (Lithium Metal)", "Lead Acid", "Lead Acid (Non-Spillable)"].includes(batteryTechnologyType)) && safetyDataSheets.length > 0 && (
                            <Box>
                              <ul style={{ marginBlock: 0, marginInline: 0, }}>
                                {safetyDataSheets.map((item, index) => (
                                  <li key={index}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', }}>
                                      <Box>
                                        {item.slice(7, 20)}...{item.slice(75, 88)}
                                      </Box>
                                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', }}>
                                        <IconButton
                                          onClick={() => handleRemoveURL(index)}
                                          sx={{ color: theme.accent.error }} // Set color for the IconButton
                                        >
                                          <CloseIcon />
                                        </IconButton>
                                      </Box>
                                    </Box>

                                  </li>
                                ))}
                              </ul>
                            </Box>
                          )}

                          {(chemicalAerosolPesticide === "Yes" || ["Lithium Ion", "Lithium Primary (Lithium Metal)", "Lead Acid", "Lead Acid (Non-Spillable)"].includes(batteryTechnologyType)) && (
                            <CustomInput
                              fullWidth
                              size="small"
                              label={"Number of Hazardous Components"}
                              value={numberOfHazardousComponents}
                              onChange={(e) => setNumberOfHazardousComponents(e.target.value)}
                              type="number"
                              inputProps={{
                                min: 0,
                                max: 1000,
                                step: 1
                              }}
                              isError={!numberOfHazardousComponents || parseInt(numberOfHazardousComponents) < 0 || parseInt(numberOfHazardousComponents) > 1000}
                            />
                          )}
                        </Box>
                      </Grid>

                      {/* State Restrictions */}
                      <Grid size={{ xs: 12, sm: 12, md: 6, lg: 4, xl: 3 }}>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                          <SelectComponent
                            value={stateRestrictionsText}
                            title={"Restriction Type"}
                            onChange={updateFormField(setStateRestrictionsText)}
                            options={[
                              { value: "None", name: "None" },
                              { value: "Illegal for Sale", name: "Illegal for Sale" },
                              { value: "Commercial", name: "Commercial" },
                            ]}
                            size={"small"}
                          />
                          {stateRestrictionsText !== "None" && (
                            <CustomInput
                              fullWidth
                              size="small"
                              label={"State Codes"}
                              value={stateRestrictionsStates}
                              onChange={(e) => updateFormField(setStateRestrictionsStates(e.target.value))}
                              type="text"
                              isError={!stateRestrictionsStates || stateRestrictionsStates === ""}
                            />
                          )}
                          {stateRestrictionsText !== "None" && (
                            <CustomInput
                              fullWidth
                              size="small"
                              label={"Zip Codes"}
                              value={stateRestrictionsZipCodes}
                              onChange={(e) => updateFormField(setStateRestrictionsZipCodes(e.target.value))}
                              type="number"
                              isError={!stateRestrictionsZipCodes || parseInt(stateRestrictionsZipCodes) === 0}
                            />
                          )}
                          {stateRestrictionsText !== "None" && (
                            <Button onClick={handleAddRestriction}>
                              <AddIcon />
                            </Button>
                          )}
                        </Box>
                        <Box sx={{ mt: 2 }}>
                          <ul>
                            {stateRestrictions.map((restriction, index) => (
                              <li key={index}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                  <Box>
                                    <strong>Type:</strong> {restriction.stateRestrictionsText}<br />
                                    <strong>States:</strong> {restriction.states}<br />
                                    <strong>ZIP Codes:</strong> {restriction.zipCodes}
                                  </Box>
                                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', }}>
                                    <IconButton
                                      onClick={() => handleRemoveRestriction(index)}
                                      sx={{ color: theme.accent.error }} // Set color for the IconButton
                                    >
                                      <CloseIcon />
                                    </IconButton>
                                  </Box>
                                </Box>

                              </li>
                            ))}
                          </ul>
                        </Box>
                      </Grid>

                      <Grid size={{ xs: 12, sm: 12, md: 6, lg: 4, xl: 3 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <SelectComponent
                            value={batteryTechnologyType}
                            title={"Contained Battery Type"}
                            onChange={setBatteryTechnologyType}
                            options={[
                              { value: "Does Not Contain a Battery", name: "Does Not Contain a Battery" },
                              { value: "Mercury", name: "Mercury" },
                              { value: "Carbon Zinc", name: "Carbon Zinc" },
                              { value: "Lead Acid (Non-Spillable)", name: "Lead Acid (Non-Spillable)" },
                              { value: "Alkaline", name: "Alkaline" },
                              { value: "Lithium Primary (Lithium Metal)", name: "Lithium Primary (Lithium Metal)" },
                              { value: "Nickel Cadmium", name: "Nickel Cadmium" },
                              { value: "Lithium Ion", name: "Lithium Ion" },
                              { value: "Nickel Metal Hydride", name: "Nickel Metal Hydride" },
                              { value: "Lead Acid", name: "Lead Acid" },
                              { value: "Silver", name: "Silver" },
                              { value: "Magnesium", name: "Magnesium" }
                            ]}
                            size={"small"}
                            required={true}
                          />

                          {batteryTechnologyType !== "Does Not Contain a Battery" && (
                            <SelectComponent
                              value={hasBatteries}
                              title={"Batteries Included"}
                              onChange={setHasBatteries}
                              options={[
                                { value: "Yes", name: "Yes" },
                                { value: "No", name: "No" }
                              ]}
                              size={"small"}
                            />
                          )}
                          {batteryTechnologyType !== "Does Not Contain a Battery" && (
                            <SelectComponent
                              value={batterySize}
                              title={"Battery Size"}
                              onChange={setBatterySize}
                              options={BATTERY_SIZES.sort().map((size) => { return { value: size, name: size } })}
                              size={"small"}
                              required={true}
                            />
                          )}
                        </Box>
                      </Grid>

                      <Grid size={{ xs: 12, sm: 12, md: 6, lg: 4, xl: 3 }}>
                        {["Lithium Ion", "Lithium Primary (Lithium Metal)"].includes(batteryTechnologyType) && (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <CustomInput
                              fullWidth
                              size="small"
                              label={"Battery Model"}
                              value={batteryModel}
                              onChange={(e) => setBatteryModel(e.target.value)}
                              type="text"
                              inputProps={{
                                minLength: 1,
                                maxLength: 400,
                              }}
                              isError={!batteryModel || batteryModel.length < 1 || batteryModel.length > 400}
                              helperText={
                                <Typography sx={styles.helperText}>1-400 characters<InfoOutlinedIcon sx={{ fontSize: '16px', }} /></Typography>
                              }
                            />
                            <SelectComponent
                              value={batteryFormFactor}
                              title={"Battery Form Factor"}
                              onChange={setBatteryFormFactor}
                              options={[
                                { value: "Battery", name: "Battery" },
                                { value: "Cell", name: "Cell" },
                                { value: "Button Cell", name: "Button Cell" }
                              ]}
                              size={"small"}
                              required={true}
                            />
                            <CustomInput
                              fullWidth
                              size="small"
                              label={"Battery Count"}
                              value={numberOfBatteries}
                              onChange={(e) => setNumberOfBatteries(e.target.value)}
                              type="number"
                              inputProps={{
                                min: 0,
                                max: 10000000,
                                step: 1
                              }}
                              isError={!numberOfBatteries || parseInt(numberOfBatteries) <= 0 || parseInt(numberOfBatteries) > 10000000}
                            />
                            <CustomInput
                              fullWidth
                              size="small"
                              label={"Battery Cell Count"}
                              value={numberOfBatteryCells}
                              onChange={(e) => setNumberOfBatteryCells(e.target.value)}
                              type="number"
                              inputProps={{
                                min: 0,
                                max: 1000,
                                step: 1
                              }}
                              isError={!numberOfBatteryCells || parseInt(numberOfBatteryCells) <= 0 || parseInt(numberOfBatteryCells) > 1000}
                            />
                            <SelectComponent
                              value={includedBatteryPackaging}
                              title={"Included Battery Packaging"}
                              onChange={setIncludedBatteryPackaging}
                              options={[
                                { value: "Loose", name: "Loose" },
                                { value: "Contained In", name: "Contained In" },
                                { value: "Packed With", name: "Packed With" }
                              ]}
                              size={"small"}
                              required={true}
                            />
                            <CustomInput
                              fullWidth
                              size="small"
                              label={"Battery Weight"}
                              value={batteryWeight}
                              onChange={(e) => setBatteryWeight(e.target.value)}
                              type="number"
                              inputProps={{
                                min: 0,
                                max: 1000000000,
                                step: 0.001
                              }}
                              isError={!batteryWeight || parseFloat(batteryWeight) <= 0}
                            />
                            {batteryTechnologyType === "Lithium Ion" ? (
                              <CustomInput
                                fullWidth
                                size="small"
                                label={"Battery Watt Hour Rating"}
                                value={batteryWattHour}
                                onChange={(e) => setBatteryWattHour(e.target.value)}
                                type="number"
                                inputProps={{
                                  min: 0,
                                  max: 1000000000,
                                  step: 0.001
                                }}
                                isError={!batteryWattHour || parseInt(batteryWattHour) <= 0}
                              />
                            ) : (
                              <CustomInput
                                fullWidth
                                size="small"
                                label={"Lithium Metal Content Weight"}
                                value={lithiumMetalContentWeight}
                                onChange={(e) => setLithiumMetalContentWeight(e.target.value)}
                                type="number"
                                inputProps={{
                                  min: 0,
                                  max: 100000000,
                                  step: 0.001
                                }}
                                isError={!lithiumMetalContentWeight || parseFloat(lithiumMetalContentWeight) <= 0}
                              />
                            )}
                          </Box>
                        )}
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>

                {/* Product Category data */}
                <Grid size={12}>
                  {schema ? (
                    <Box sx={{ mb: 4 }}>
                      <SchemaForm
                        schema={schema}
                        currProduct={currProduct}
                        productType={currProduct.productType}
                        formData={formData}
                        setFormData={setFormData}
                        isConvert={!skuUpdate}
                      />
                    </Box>
                  ) : loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                      <EnhancedSpinningLogo />
                    </Box>
                  ) : (
                    <Alert severity="info">
                      <Typography sx={{ fontFamily: theme.fontFamily }}>Please select Product Type</Typography>
                    </Alert>
                  )}
                </Grid>

              </Grid>

              <Box sx={{ position: 'sticky', bottom: 16, right: 16, display: 'flex', justifyContent: 'flex-end', zIndex: 100, width: 'auto', ml: 'auto', }} >
                <Button
                  variant="contained"
                  sx={{
                    // width: '100%',
                    mb: 1,
                    textTransform: 'none',
                    fontFamily: theme.fontFamily,
                    border: `1px solid ${theme.border.light}`,
                    borderRadius: '10px',
                    color: theme.text.negative,
                    backgroundColor: theme.color.primary,
                  }}
                  onClick={handleAddItem}
                  disabled={loadingAddItem}
                >
                  {loadingAddItem ? (
                    <CustomCircularProgress color={theme.text.negative} />
                  ) : (
                    <>
                      Add Item
                    </>
                  )}
                </Button>
              </Box>
            </Box>
          )}
        </>
      )}

      <QuestionModal
        open={openQuestionModal}
        setOpen={setOpenQuestionModal}
        message={`Hey, we found ${sfInventory} items in your inventory for SKU: ${sku.trim()}. Would you like to set them to zero?`}
        confirm={() => { handleZeroSFQty(); setOpenQuestionModal(false); }}
        cancel={() => { addItem(); setOpenQuestionModal(false); }}
      />


      <SafetyDataSheetsModal
        open={openSafetyModal}
        setOpen={setOpenSafetyModal}
        options={safetyDataSheetOptions}
        onSelect={handleSelectURL}
        handleAddSafetySheet={handleAddSafetySheet}
      />

      <ImagesModal
        open={openImgModal}
        setOpen={setOpenImgModal}
        images={currProduct.images}
        onSelect={handleSelectImage}
      />

      <SKUBuilderModal
        open={openSkuBuilderModal}
        setOpen={setOpenSkuBuilderModal}
        confirm={() => { localStorage.setItem('IFSKUInputs', JSON.stringify(skuInputs)); localStorage.setItem('IFSKUSeperator', skuSeperator) }}
        inputs={skuInputs}
        setInputs={setSkuInputs}
        seperator={skuSeperator}
        setSeperator={setSkuSeperator}
        reset={resetSkuInputs}
      />

      <ErrorModal open={openErrorModal} setOpen={setopenErrorModal} message={errorMessage} />
    </Box >
  );
}

export default MobileForm;
